// The sprite atlas: every mark of the map, rasterised once with Canvas 2D
// and packed into ONE texture (the plan's A.2 and A.7).
//
// The PixiJS client builds one texture per master with
// `renderer.generateTexture`, which crops to the ink and anchors at the
// cropped-bounds centre. This module reproduces both: it measures a spec in
// a scratch canvas by an alpha scan, and the entry records the cropped box
// AND where that box's centre sits against the drawing origin. A layer then
// picks the placement the PixiJS layer used — centred for a hull, a bloom
// or a badge, origin-relative for a site mark or the captain's chevron.
//
// Nothing here needs a renderer, so the marks come straight from
// `render/marks.ts` through its `Canvas2DMarks` shim.

import { FALLBACK_MAX_TEXTURE, type AtlasTexture } from './types';
import { SPAN_LETTERS } from '../../locale/span';
import {
  Canvas2DMarks, COMBAT_ICONS, SUPPORT_ICONS, dartPath,
  depositInk, depositLetter, drawCP, drawDepositStyle,
  drawMarkerArrow, drawMarkerCircle, hexPath,
  type HullPath,
} from '../../render/marks';
import { rgba } from '../../render/color';
// The faction marks of round 075 (R1568): the badge of a site and of a hull.
import { drawFactionBadge, siteBadgeAt } from '../../render/faction-marks';
import type { MarkGraphics } from '../../render/marks';
// The friendly marks of an own hull and a squadmate hull (R185, R830).
import { FLEET_MARK_CELLS } from '../../render/fleet-marks';
// The six hold symbols and the mark of an installation (R633, R641).
import {
  HOLD_SYMBOL_IDS,
  drawHoldLight, drawHoldMark, drawHoldRuin,
  drawInstallationMark, drawInstallationRuin,
  type HoldEra, type HoldRank, type HoldSymbolId,
} from '../../render/hold-marks';
// The five own hold marks and the five squad hold marks (R185, R834).
import {
  HOLD_ANIM_PARTS, isOwnHoldMark, isSquadHoldMark,
} from '../../render/hold-rank-marks';
// The ten structure symbol sets (R1103, R1104).
import {
  STRUCTURE_SYMBOL_IDS, drawStructureMark, structureKindOf,
  type StructureSymbolId,
} from '../../render/structures';
import type {
  DepositStyle, DigitStyle, OwnHoldStyle, ShipStyle, SquadHoldStyle,
} from '../../ui/settings';
// The twelve ship icon sets (R835). The AUTO set is baked eagerly below; any
// other set bakes on the first frame that asks for it.
import {
  DEFAULT_SHIP_STYLE, HULL_FIELD_OF_CLASS, hullPathOf,
} from '../../render/hull-styles';
import { DEPOSIT_STYLES, SHIP_STYLES } from '../../gen/balance';
// The objective tell of an arcade structure (R2029).
import { OBJECTIVE_FLAG_KEY, paintObjectiveFlag } from '../../render/objective-flag';
// The dart of a drill spot (R1573).
import {
  DART_CELL_LEN, DART_CELL_W, DRILL_DART_KEY, paintDrillDart,
} from '../../render/drill-dart';
// The commission mark of v2 phase 5 (R2645).
import { COMMISSION_MARK_KEY, commissionBox, paintCommissionMark } from '../../render/commission-mark';
// The livery of the Fleet Designer (R1252): the one bake of every option.
import { bakeLivery, hullSpan, liveryOfCode, type LiveryMaster } from '../../render/livery';
// The shapes come from `torpedo-defs.ts` and NOT from `torpedo-styles.ts`
// (R431): the styles module builds PixiJS textures, and one value import of
// it would put the whole library back into this bundle.
import { paint as paintTorpedo, SHAPE as TORPEDO_SHAPE, type TexKey } from '../../render/torpedo-defs';
import {
  auraCellFits, auraCellSize, type AuraFigures,
} from '../../render/aura-styles';
import { EFFECT_CELL_PX } from '../../render/effect-styles';
import {
  COMBAT_CELL_PX, COMBAT_DART_LEN, COMBAT_PLATE_HALF,
} from '../../render/combat-recipe-kit';
import {
  GATHER_CELL_PX, GATHER_CHIP_HALF_X, GATHER_CHIP_HALF_Y, GATHER_HALO_OUTER,
  GATHER_HALO_WIDTH, GATHER_WEDGE_LEN,
} from '../../render/gather-recipe-kit';
// The fluke cell of the anchoring look `bubble-column` (R1128).
import { anchorGroundSpecFor } from '../../render/anchor-recipes-ground';
// The gradient, hex, shard and boil cells of the detonation looks (R2079).
import { detonationSpecFor } from '../../render/detonation-cells';
import { seaObjectSpecFor } from '../../render/seaobject-cells';
import { poiSpecFor } from '../../render/poi-cells';
import { fairwaySpecFor } from '../../render/fairway-cells';
// The ghost Fanal of a lane gap on chart card 16 (R3066, R3072).
import { ghostLightSpecFor } from '../../render/lane-light-looks';
// The four kraken bodies, as the loop sets the LAYER also strokes (R525).
import { KRAKEN_BODIES, type KrakenBody } from '../../render/kraken-shapes';
// The aspect chevrons of a hull and of the damage figure (R2407).
import {
  CHEVRON_FLASH_KEY, CHEVRON_GAPS, CHEVRON_KEY, chevronHalf, chevronSetHalf,
  chevronSetKey, drawChevronSet, drawFigureChevron, drawFigureDiamond, drawOneChevron,
} from '../../render/aspect-chevrons';
// The drawn group digits (R833). `mono` keeps the font path below.
import { DIGIT_STYLE_DEFS, segBoxWidth, segDigit, type DigitValue } from '../../render/digit-paths';
import { Brotherhood, NpcKind, ShipClass } from '../../gen/proto/aquaphobia';
import {
  MONSTER, NPC_BOX, NPC_STROKE,
  paintPirateHull, paintPirateLight, type PirateInks, type PirateLight,
} from '../../render/npc-marks';
import { factionColor, factionOf, hexCorner } from '../../world';
import {
  ACCENT_COLOR,
  CHART,
  CONTROL_PIP,
  FLEET_BLOOM_HALF_LEN, FLEET_BLOOM_HALF_W,
  FLEET_GROUP_BADGE_FONT, FLEET_GROUP_BADGE_FONT_FALLBACK,
  FLEET_GROUP_BADGE_H, FLEET_GROUP_BADGE_WEIGHT,
  FLEET_ASPECT,
  FLEET_AURA,
  FLEET_HOLLOW_STROKE_PX,
  FLEET_NPC_MARK_PX,
  NPC_INK,
  DAMAGE_FIGURE,
  DEPOSIT_MARKS,
  FACTION_MARKS,
  HAZE_BANDS, HAZE_COLOR, HAZE_FAR_ALPHA, HAZE_NEAR_ALPHA,
  MAP_PALETTE,
  MARK_INKS,
} from '../../gen/presentation';
import { LIGHT } from '../../identity/pick';
import {
  paintMarkerCell, readMarkerCellKey, type MarkerInks,
} from '../../render/map-marker-cells';
import { TAU } from '../../render/math';
import { oneOf } from '../../util/oneof';

// ---------------------------------------------------------------------------
// The shelf packer
// ---------------------------------------------------------------------------

/**
 * A shelf packer: cells go left to right on a shelf, and a cell that does
 * not fit opens the next shelf at the height of the tallest cell so far.
 *
 * It is the simplest packer that wastes little on a set of marks that are
 * all of a similar height, and it never moves a cell it has placed.
 */
export class ShelfPacker {
  private penX = 0;
  private shelfY = 0;
  private shelfH = 0;

  constructor(public width: number, public height: number) {}

  /** Places a cell, or returns null when the sheet is full. */
  alloc(w: number, h: number): { x: number; y: number } | null {
    if (w > this.width || h > this.height) return null;
    if (this.penX + w > this.width) {
      this.shelfY += this.shelfH;
      this.shelfH = 0;
      this.penX = 0;
    }
    if (this.shelfY + h > this.height) return null;
    const at = { x: this.penX, y: this.shelfY };
    this.penX += w;
    if (h > this.shelfH) this.shelfH = h;
    return at;
  }

  reset(width = this.width, height = this.height): void {
    this.width = width;
    this.height = height;
    this.penX = 0;
    this.shelfY = 0;
    this.shelfH = 0;
  }
}

// ---------------------------------------------------------------------------
// Entries and specs
// ---------------------------------------------------------------------------

/** One packed mark. */
export interface AtlasEntry {
  key: string;
  /** The texture rectangle, inset by half a texel on every side. */
  u0: number; v0: number; u1: number; v1: number;
  /** The cropped box in world px at scale 1. */
  w: number; h: number;
  /** Where the box centre sits against the drawing origin, in world px. */
  ox: number; oy: number;
  /**
   * A glyph cell's advance in px at scale 1, which the pen adds after it
   * (R548). A mark that is not a glyph has none.
   */
  adv?: number;
  /** A glyph cell's baseline, in px under the cell TOP at scale 1. */
  base?: number;
}

/** How to draw one mark. */
export interface AtlasSpec {
  key: string;
  /** Supersampling. Hulls and digits 4, site marks 2, gradients 1. */
  scale: number;
  /**
   * Draws the mark. The context is already scaled and its origin is the
   * mark's own origin, and a path is already open.
   */
  draw(ctx: CanvasRenderingContext2D): void;
  /**
   * A fixed box in world px against the origin. With it the bake skips the
   * alpha scan, which is what a badge needs: five digits must share one box
   * or they would not sit on one axis.
   */
  box?: { x: number; y: number; w: number; h: number };
  /**
   * Collapse the texture rectangle to the middle texel. The `white` cell
   * uses it, so a flat rectangle samples one solid texel at any size.
   */
  collapse?: boolean;
  /** A glyph cell's advance and baseline, which the entry carries (R548). */
  adv?: number;
  base?: number;
}

/** The scratch sheet a spec is measured in, in device px. */
const SCRATCH_PX = 512;

/** Empty texels around a cell. Level 3 of the mip chain still has one. */
export const CELL_PAD = 8;

/**
 * Transparent texels the bake keeps between the ink and the crop bound.
 *
 * `entryUV` stops the texture rectangle at the centre of the outer texel,
 * so the quad's own edge samples that texel. Ink that reaches the crop
 * bound is therefore drawn at full alpha on the edge of the quad, and only
 * MSAA gave it a ramp. The white mast of a hold is such a mark: its butt
 * cap lands on a texel boundary, so the top row of the crop is solid.
 *
 * One transparent texel gives the bilinear tap a ramp of one texel, which
 * is a half world px at the site scale. The ink itself does not move: the
 * pad is the same on the two opposite sides, so the entry's offset holds
 * and the box grows by one texel a side.
 *
 * A spec with a fixed `box` keeps its box and takes no pad, because the box
 * IS the placement: the five badge digits must share one.
 */
export const INK_PAD = 1;

/** Inclusive bounds of the ink in scratch px. */
export interface InkBounds { minX: number; minY: number; maxX: number; maxY: number }

/**
 * Turns scratch-px ink bounds into the entry's box. Pure, so a test pins the
 * arithmetic without a canvas.
 */
export function entryGeometry(
  bounds: InkBounds, scale: number, originX: number, originY: number, pad = 0,
): { cropW: number; cropH: number; w: number; h: number; ox: number; oy: number } {
  const cropW = bounds.maxX - bounds.minX + 1 + pad * 2;
  const cropH = bounds.maxY - bounds.minY + 1 + pad * 2;
  return {
    cropW,
    cropH,
    w: cropW / scale,
    h: cropH / scale,
    ox: (bounds.minX - pad + cropW / 2 - originX) / scale,
    oy: (bounds.minY - pad + cropH / 2 - originY) / scale,
  };
}

/**
 * The texture rectangle of a cell, inset by half a texel so the quad's
 * corner samples land on the first and last texel centres.
 */
export function entryUV(
  x: number, y: number, w: number, h: number, texW: number, texH: number,
): { u0: number; v0: number; u1: number; v1: number } {
  return {
    u0: (x + 0.5) / texW,
    v0: (y + 0.5) / texH,
    u1: (x + w - 0.5) / texW,
    v1: (y + h - 0.5) / texH,
  };
}

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

const flag = (b: boolean) => (b ? '1' : '0');

// The hull cell carries the SET the class wears (R835), because a set draws a
// different silhouette for the same class. The dart, which is the mark of a
// contact whose class the server withheld, is the same shape in every set, so
// it is baked ONE time under the `default` set and every caller asks for it
// under that set.
export const hullKey = (style: ShipStyle, cls: number, hollow: boolean) =>
  `hull:${style}:${cls}:${flag(hollow)}`;
/**
 * Every class the atlas holds a hull cell for: the dart and the classes of
 * `buildAtlasSpecs`. A layer asks `hullKey` for these alone, and any other
 * class wears the dart. A monster class (16 to 19) has no hull cell, so a
 * key of one throws in `Atlas.get` (R3194).
 */
export const HULL_CLASSES: ReadonlySet<number> = new Set<number>([
  ShipClass.SHIP_CLASS_UNSPECIFIED,
  ...SUPPORT_ICONS.map(([c]) => c as number),
  ...COMBAT_ICONS.map(([c]) => c as number),
]);
// A liveried hull master carries the livery code as a fifth part (R1252,
// §6.1.6): `hull:<set>:<cls>:<hollow>:<livery>`. The mask cell of the
// overlay quad is `livery:<set>:<cls>:<livery>`. Both bake on first use.
export const liveryHullKey = (style: ShipStyle, cls: number, hollow: boolean, code: string) =>
  `hull:${style}:${cls}:${flag(hollow)}:${code}`;
export const liveryMaskKey = (style: ShipStyle, cls: number, code: string) =>
  `livery:${style}:${cls}:${code}`;
// The badge cell carries the VALUE of the `digits` field (R833), because a
// value bakes a different digit: `mono` sets the vendored face and the four
// seven-segment values draw their own alphabet.
export const badgeKey = (style: DigitStyle, n: number) => `badge:${style}:${n}`;
export const torpedoKey = (k: TexKey) => `torp:${k}`;
// The two marks of the NPC side (v2-the-deep.md §8, R2637): a hull of the
// NPC side wears its kind and no class silhouette, so a pirate group of
// warships and a monster each keep one shape on the map.
export const NPC_PIRATE_KEY = 'npc:pirate';
export const NPC_MONSTER_KEY = 'npc:monster';
export const npcKey = (kind: NpcKind) =>
  (kind === NpcKind.NPC_KIND_MONSTER ? NPC_MONSTER_KEY : NPC_PIRATE_KEY);
// A pirate hull on the lean swallowtail with its class part and the mark of
// its brotherhood (round 113, R2720): `npc:pirate:<cls>:<bro>`, baked on
// demand. A hull with no class and no brotherhood keeps the eager cell of
// R2637, so a pirate of before the build draws as it drew.
export const pirateHullKey = (cls: number, bro: number) =>
  (cls === 0 && bro === 0 ? NPC_PIRATE_KEY : `npc:pirate:${cls}:${bro}`);
// A light of a brotherhood mark (card 01 lantern, card 11 knot light),
// which the layer draws at an alpha each frame: `npc:light:<light>`.
export const pirateLightKey = (light: PirateLight) => `npc:light:${light}`;
// The five site keys of a hold and of a faction installation (R633). A hold
// wears one of the six symbols in one of the two readings, so the symbol,
// the reading and the rank are all part of the key and the colour is baked.
//
// `mark` is the hold mark of R834: the `ownHold` value at the own rank, the
// `squadHold` value at the mate rank, and `-` at the two plain ranks, which
// wear no mark at all. It joins the key because the mark is baked into the
// cell under the symbol, and it carries no default: a key that named the
// wrong mark for its rank would resolve to nothing and bake no cell.
//
// `charge` bakes the faction charge of card 01 of round 075 beside the
// symbol (R1568): a hold of ANOTHER faction wears it, so only the `other`
// rank may name it, and the key then carries a last part `c`.
export const siteHoldKey = (
  symbol: HoldSymbolId, era: HoldEra, faction: number, rank: HoldRank,
  mark: OwnHoldStyle | SquadHoldStyle | '-', charge = false,
) => `site:hold:${symbol}:${era}:${faction}:${rank}:${mark}${charge ? ':c' : ''}`;
export const siteHoldLightKey = (symbol: HoldSymbolId, era: HoldEra) =>
  `site:holdlight:${symbol}:${era}`;
export const siteHoldRuinKey = (symbol: HoldSymbolId, era: HoldEra) =>
  `site:holdruin:${symbol}:${era}`;
export const siteInstKey = (era: HoldEra, faction: number, own: boolean) =>
  `site:inst:${era}:${faction}:${flag(own)}`;
// The charge pip astern of a hull of another faction (R1568, card 02 of
// round 075): one cell per reading and faction, baked in its own inks, so
// the quad carries no tint. Five factions and two readings are ten cells.
export const factionPipKey = (era: HoldEra, faction: number) => `fpip:${era}:${faction}`;
export const siteInstRuinKey = (era: HoldEra) => `site:instruin:${era}`;
// A deposit wears one of the six values of the `deposit` field (R832), so
// the value is part of the key beside the commodity and the fill step.
export const siteDepositKey = (
  style: DepositStyle, resource: number, level: number,
) => `site:deposit:${style}:${resource}:${level}`;
/** The prefix of every cell whose letter the chart face re-draws (R832). */
export const SITE_DEPOSIT_TAG_PREFIX = 'site:deposit:tag:';
// A control point wears the structure symbol of R1103, keyed by the object
// KIND (R1104). `set` is the `structure` field in force, `kind` is the
// `ObjKind` number the Sieges frame states (0 while the layer has had no
// frame), `faction` is the holder and `attacker` is the faction that pledged,
// or 0 while no pledge stands. Both factions are part of the key, because
// every card of round 054 paints the tell in the attacker's ink and the atlas
// bakes the colour. A map holds ten objects, so about ten cells live at once.
export const siteCpKey = (
  set: StructureSymbolId, kind: number, faction: number, attacker: number,
) => `site:cp:${set}:${kind}:${faction}:${attacker}`;

/** The digits the badge alphabet holds (`render/fleets.ts` `BADGE_DIGITS`). */
export const BADGE_DIGITS = 6;

/**
 * The two metrics of the vendored badge face, in ems.
 *
 * JetBrains Mono states `unitsPerEm 1000`, `sCapHeight 730` and one advance
 * width of 600. They are facts about the file in `client/public/fonts/`,
 * and `render/fleets.ts:211-233` states them the same way for the PixiJS
 * client. They are duplicated here because that module imports PixiJS and
 * the fast bundle must not.
 */
const BADGE_CAP_EM = 0.730;
const BADGE_ADVANCE_EM = 0.600;
/** The size a badge is set at, so the DIGIT measures `group_badge_h` px. */
const BADGE_FONT_PX = FLEET_GROUP_BADGE_H / BADGE_CAP_EM;
/** The monospaced advance, which is the width of one badge box. */
const BADGE_W = BADGE_FONT_PX * BADGE_ADVANCE_EM;

/** A family name, quoted when it holds a space, for a canvas `font`. */
const quoteFamily = (f: string) => (f.includes(' ') ? `"${f}"` : f);

/** The family stack of the vendored face, quoted for a canvas `font`. */
function monoFamilies(): string {
  return [FLEET_GROUP_BADGE_FONT, ...FLEET_GROUP_BADGE_FONT_FALLBACK]
    .map(quoteFamily).join(', ');
}

/** The font shorthand the badge is set in. */
export function badgeFont(): string {
  return `${FLEET_GROUP_BADGE_WEIGHT} ${BADGE_FONT_PX}px ${monoFamilies()}`;
}

// ---------------------------------------------------------------------------
// The damage figure's glyph cells (R538)
// ---------------------------------------------------------------------------
//
// A damage figure is canvas text, which §1 otherwise forbids. It takes the
// carve-out the control-group badge took (R427): the atlas bakes ONE cell a
// glyph a weight, and the layer draws one quad a glyph. The cells are the
// twelve code points the vendored subset holds — the ten digits, the
// hyphen-minus and the plus — at the two weights `DAMAGE_FIGURE` names. The
// plus is the mark a heal rises off a bar (R724); it takes the same cells,
// because it is set in the same face at the same size.
//
// The ink is WHITE and the halo is the near-black of the water. The quad
// shader multiplies the cell by the tint, so white takes the figure's ink
// and near-black stays near-black whatever the tint is: one cell serves the
// taken red and the dealt white alike.
//
// The light identity needs a PAPER halo under a dark glyph
// (ui-map-interface.md §6.0, R3296). A paper halo in a tinted cell would
// take the tint like the glyph does, so the light identity bakes a glyph
// cell in its own ink: `glyphCellKey` names the cell of one glyph in one
// ink, and the layer draws it with the tint `glyphTint` gives, which is no
// tint at all. A frame draws the same quads as in the dark identity, and
// each inked cell bakes once, the first time a layer asks for it.

/**
 * The glyphs one figure is spelled from.
 *
 * The digits and the two signs are the damage figure's own (R538). `F`, `U`
 * and `L` join them for the one word the gather figure prints, `FULL`
 * (R1123), and `:` and `h` for the anchoring numerals (R1125). The unit
 * letters of the span forms (`SPAN_LETTERS`, `smhd` in English) join them,
 * so an anchoring count writes its units (R2741). One alphabet, one face
 * and one set of cells serve every figure, so the map bakes no second
 * numeral face. A space is no cell: a run steps over it.
 */
export const DAMAGE_GLYPHS = [...new Set('0123456789-+FUL:h' + SPAN_LETTERS)].join('');

/**
 * The halo colour: the water black of `ui-map-interface.md` §2, and the
 * paper of the light identity (`mark_inks.halo`, R3296).
 *
 * The damage figure and the chart glyph (§17.4) both stroke in it. Each
 * one carries its own width and its own alpha.
 */
const DAMAGE_HALO_INK = MARK_INKS.halo;
/** Whether a tinted glyph cell bakes its halo: in the dark identity only
 *  (R3296). The light identity draws an inked cell instead, and an inked
 *  cell always bakes its halo. */
const GLYPH_HALO = !LIGHT;

/** The paint of a glyph cell: the mask, or the ink of an inked cell. */
const glyphFill = (ink: number | null): string => (ink === null ? '#ffffff' : rgba(ink, 1)); // mask

/** The last part of the key of an inked glyph cell: `:i<rrggbb>`. */
const INKED = /^(.*):i([0-9a-f]{6})$/;

/** The key of one glyph cell in one ink. */
export const inkedGlyphKey = (key: string, ink: number): string =>
  `${key}:i${(ink & 0xffffff).toString(16).padStart(6, '0')}`; // mask: the 24 bits

/**
 * How many inks one glyph cell may take in the light identity. A layer
 * whose ink moves every frame (a lift that eases) would bake a cell a
 * frame; past this count the glyph takes its tinted cell with no halo.
 */
const INKED_MAX = 48;
/** The inked keys built so far: ink, then the plain key. */
const inkedKeys = new Map<number, Map<string, string>>();

/**
 * The cell a layer draws one glyph of a figure from, in the ink `ink`: the
 * tinted mask in the dark identity, and the cell baked in `ink` over the
 * paper halo in the light one (R3296). It builds a key string once an ink
 * and a glyph, so a frame allocates nothing.
 */
export function glyphCellKey(key: string, ink: number): string {
  if (!LIGHT) return key;
  let byKey = inkedKeys.get(ink);
  if (byKey === undefined) {
    if (inkedKeys.size >= INKED_MAX) return key;
    byKey = new Map();
    inkedKeys.set(ink, byKey);
  }
  let k = byKey.get(key);
  if (k === undefined) {
    k = inkedGlyphKey(key, ink);
    byKey.set(key, k);
  }
  return k;
}

/** The tint of a glyph quad: the ink in the dark identity, and none in the
 *  light one, where an inked cell carries the ink (R3296). An ink past
 *  `INKED_MAX` keeps its tint, because its cell is the mask. */
export function glyphTint(ink: number): number {
  if (!LIGHT) return ink;
  const byKey = inkedKeys.get(ink);
  return byKey === undefined ? ink : 0xffffff; // mask
}
/** How opaque the halo is. The mockup's `text()` strokes at this alpha. */
const DAMAGE_HALO_ALPHA = 0.85;

export const damageGlyphKey = (weight: number, ch: string) => `dmg:${weight}:${ch}`;
export const DAMAGE_DART_KEY = 'dmg:dart';
export const DAMAGE_DART_HOLLOW_KEY = 'dmg:dart-hollow';
/** The chevron of the aspect suffix and the diamond of an ambush (R2407). */
export const DAMAGE_CHEV_KEY = 'dmg:chev';
export const DAMAGE_DIAMOND_KEY = 'dmg:diamond';

/** The two weights the atlas bakes: the gun figure's and the torpedo's. */
export const DAMAGE_WEIGHTS = [DAMAGE_FIGURE.weight, DAMAGE_FIGURE.torpedoWeight] as const;

/**
 * The size one weight's cells are baked at, in px.
 *
 * A gun figure is set at `size_px` and a torpedo figure at `torpedo_scale`
 * of it, so each weight is baked at the size it is normally drawn at and
 * only a splash — weight 700 at the gun size — scales its cells down.
 */
export function damageFontPx(weight: number): number {
  return weight === DAMAGE_FIGURE.torpedoWeight
    ? DAMAGE_FIGURE.sizePx * DAMAGE_FIGURE.torpedoScale
    : DAMAGE_FIGURE.sizePx;
}

/**
 * The monospaced advance of one glyph at a display size, in px.
 *
 * The face states one advance width of 600/1000 em for every glyph it
 * carries, so the layer lays a figure out by arithmetic and never measures.
 */
export const damageAdvancePx = (px: number) => px * BADGE_ADVANCE_EM;

/** The dart glyph, as `dart()` of mockup round thirty draws it. */
export const DAMAGE_DART_H_EM = 0.56;
export const DAMAGE_DART_LEN_K = 2.4;
/** What stands between the dart and the first digit, in ems. */
export const DAMAGE_DART_GAP_EM = 0.30;
/** The size both dart cells are baked at: the torpedo figure's own size. */
export const DAMAGE_DART_PX = DAMAGE_FIGURE.sizePx * DAMAGE_FIGURE.torpedoScale;

/** The font shorthand one damage weight is set in. */
export function damageFont(weight: number): string {
  return `${weight} ${damageFontPx(weight)}px ${monoFamilies()}`;
}

/**
 * The torpedo silhouette before the digits: a capsule with a tail fin,
 * nose to +x. Filled for a direct hit, hollow for a splash.
 */
function drawDamageDart(
  ctx: CanvasRenderingContext2D, h: number, hollow: boolean, ink: number | null = null,
): void {
  const L = h * DAMAGE_DART_LEN_K;
  const r = h / 2;
  ctx.beginPath();
  ctx.moveTo(-L / 2 + r, -r);
  ctx.lineTo(L / 2 - r * 1.6, -r);
  ctx.quadraticCurveTo(L / 2, 0, L / 2 - r * 1.6, r);
  ctx.lineTo(-L / 2 + r, r);
  ctx.lineTo(-L / 2, r * 1.4);
  ctx.lineTo(-L / 2, -r * 1.4);
  ctx.closePath();
  if (hollow) {
    // The outline IS the ink, so it is baked white and takes the tint.
    ctx.lineWidth = 1;
    ctx.strokeStyle = glyphFill(ink);
    ctx.stroke();
    return;
  }
  if (GLYPH_HALO || ink !== null) {
    ctx.lineWidth = 2;
    ctx.strokeStyle = rgba(DAMAGE_HALO_INK, 0.8);
    ctx.stroke();
  }
  ctx.fillStyle = glyphFill(ink);
  ctx.fill();
}

/** One glyph of the damage figure, in the mask or in an ink (R538, R3296). */
function damageGlyphSpec(key: string, weight: number, ch: string, ink: number | null): AtlasSpec {
  const px = damageFontPx(weight);
  const adv = damageAdvancePx(px);
  const halo = DAMAGE_FIGURE.haloPx;
  const font = damageFont(weight);
  return {
    key,
    scale: BADGE_SCALE,
    box: centredBox(adv + halo, px + halo),
    draw(ctx) {
      ctx.font = font;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if (GLYPH_HALO || ink !== null) {
        ctx.lineJoin = 'round';
        ctx.lineWidth = halo;
        ctx.strokeStyle = rgba(DAMAGE_HALO_INK, DAMAGE_HALO_ALPHA);
        ctx.strokeText(ch, 0, 0);
      }
      ctx.fillStyle = glyphFill(ink);
      ctx.fillText(ch, 0, 0);
    },
  };
}

/** The chevron or the diamond of the aspect, in the mask or in an ink. */
function damageMarkSpec(key: string, ink: number | null): AtlasSpec {
  const base = ink === null ? key : (INKED.exec(key)?.[1] ?? key);
  const px = DAMAGE_FIGURE.sizePx;
  const halo = DAMAGE_FIGURE.haloPx;
  return {
    key,
    scale: BADGE_SCALE,
    box: centredBox(px + halo, px + halo),
    draw(ctx) {
      for (const under of GLYPH_HALO || ink !== null ? [true, false] : [false]) {
        ctx.strokeStyle = under ? rgba(DAMAGE_HALO_INK, DAMAGE_HALO_ALPHA) : glyphFill(ink);
        if (base === DAMAGE_CHEV_KEY) {
          drawFigureChevron(ctx, px, FLEET_ASPECT.figureStrokePx + (under ? halo : 0));
        } else {
          drawFigureDiamond(ctx, px, FLEET_ASPECT.figureDiamondPx + (under ? halo : 0));
        }
      }
    },
  };
}

/** One dart of the damage figure, in the mask or in an ink. */
function damageDartSpec(key: string, hollow: boolean, ink: number | null): AtlasSpec {
  const h = DAMAGE_DART_PX * DAMAGE_DART_H_EM;
  return {
    key,
    scale: BADGE_SCALE,
    box: centredBox(h * DAMAGE_DART_LEN_K + 2, h * 1.4 + 2),
    draw: (ctx) => drawDamageDart(ctx, h, hollow, ink),
  };
}

/**
 * One inked glyph cell (R3296): `<glyph key>:i<rrggbb>`, where the glyph
 * key is a damage glyph, a damage mark or a chart glyph. It bakes the
 * glyph in the ink over the halo in `MARK_INKS.halo`, the paper of the
 * light identity, so the quad draws it with no tint.
 */
export function inkedGlyphSpecFor(key: string): AtlasSpec | null {
  const m = INKED.exec(key);
  if (!m) return null;
  const base = m[1];
  const ink = Number.parseInt(m[2], 16);
  if (base === DAMAGE_DART_KEY || base === DAMAGE_DART_HOLLOW_KEY) {
    return damageDartSpec(key, base === DAMAGE_DART_HOLLOW_KEY, ink);
  }
  if (base === DAMAGE_CHEV_KEY || base === DAMAGE_DIAMOND_KEY) return damageMarkSpec(key, ink);
  const p = base.split(':');
  if (p[0] === 'dmg' && p.length === 3) {
    const weight = Number(p[1]);
    if (!(DAMAGE_WEIGHTS as readonly number[]).includes(weight)) return null;
    if (p[2].length !== 1 || !DAMAGE_GLYPHS.includes(p[2])) return null;
    return damageGlyphSpec(key, weight, p[2], ink);
  }
  if (p[0] === 'chart') return chartGlyphSpec(key, base, ink);
  return null;
}

// ---------------------------------------------------------------------------
// The chart furniture's glyph cells (R548)
// ---------------------------------------------------------------------------
//
// The map text of the chart furniture is the THIRD canvas-text carve-out,
// after the control-group badge (R427) and the damage figure (R538). It
// works the same way: the atlas bakes one cell a glyph, and a layer draws
// one quad a glyph. Nothing here writes DOM text on the canvas.
//
// Two things are new. A chart writes WORDS, so the cells are not a fixed
// alphabet: a key names the face, the size and the code point, and the
// resolver bakes the cell the first time a piece asks for it. A page that
// draws no chart text therefore bakes no chart cell, and the arcade —
// which draws none of the furniture — pays nothing at all.
//
// The second new thing is the ADVANCE. The badge and the damage figure are
// monospaced, so a layer lays a figure out by arithmetic. The Fell face is
// proportional, so the entry records the advance the bake measured, and a
// layer asks the atlas for it (`glyphAdvance`).
//
// The ink is WHITE over a near-black halo, as R538 states it: the quad
// shader multiplies the cell by the tint, so ONE cell serves every colour a
// piece writes in.

/** The two faces the chart furniture writes in. */
export const CHART_FACES = ['mono', 'fell'] as const;
export type ChartFace = (typeof CHART_FACES)[number];

/**
 * The vendored mono face, before the badge face's own fallback stack.
 *
 * `client/entries/index.html` declares the family from
 * `jetbrains-mono-500-chart.woff2`, and `data/presentation/chart.toml`
 * `[chart.text]` names it (R548). The fallback is the badge's, because both
 * want the same thing: a monospaced face whose advance is close to 600/1000
 * em.
 */
export const CHART_MONO_FAMILY = [
  CHART.text.monoFamily, ...FLEET_GROUP_BADGE_FONT_FALLBACK,
].map(quoteFamily).join(', ');

/**
 * The vendored italic face of the historical reading, and its fallback.
 *
 * Both come from `[chart.text]`, so the pack states the face the atlas
 * bakes and the face `client/entries/index.html` declares in one place.
 */
export const CHART_FELL_FAMILY = [CHART.text.fellFamily, ...CHART.text.fallback]
  .map(quoteFamily).join(', ');

/** The advance of the mono face, in ems. It is `BADGE_ADVANCE_EM` (R427). */
export const CHART_MONO_ADVANCE_EM = BADGE_ADVANCE_EM;

/** What a glyph advances when no 2D context can measure it (jsdom). */
export const CHART_FALLBACK_ADVANCE_EM = 0.5;

/**
 * The halo width, in px.
 *
 * `data/presentation/chart.toml` `[chart.text]` owns this figure (R548).
 */
export const CHART_HALO_PX = CHART.text.haloPx;

/**
 * How opaque the halo is.
 *
 * `data/presentation/chart.toml` `[chart.text]` owns this figure. The
 * owner asked for a weaker border on 2026-09-06 (R586), so the chart
 * cell no longer strokes at the damage figure's alpha.
 */
export const CHART_HALO_ALPHA = CHART.text.haloAlpha;

/** Supersampling, as the badge and the damage glyphs take it. */
// Two, and not the badge's four: a chart cell is drawn at about the size it
// was baked at, on a screen of DPR 2, so two texels a CSS px is one texel
// a device px. Four filled a 2048 px atlas with fifteen sizes of two faces
// and threw the pieces off the map (R548).
const CHART_SCALE = 2;
/** The cell height in ems: an ascent of one em and a descent of 0.3. */
export const CHART_LINE_EM = 1.3;
/** Where the baseline sits under the cell top, in ems. */
export const CHART_ASCENT_EM = 1.0;
/** The size a face is probed at. Any size proves the file landed. */
const CHART_PROBE_PX = 13;

/** The fifth part of the key of a glyph cell that bakes no halo (R1439). */
const CHART_BARE = 'bare';

/**
 * The key of one glyph cell: the face, the size in px and the code point.
 *
 * `halo = false` names the BARE cell: the same box and the same ink, with no
 * halo stroke under the ink. The legend of the hold-fire look `restricted-line`
 * writes in it, because its card draws the text with no halo (R1439). Every
 * other chart text keeps the halo.
 */
export function chartGlyphKey(face: ChartFace, sizePx: number, ch: string, halo = true): string {
  // One decimal, so the label step's 9.5 px bakes its own cell and does not
  // take the 10 px one (R1796). A whole size writes no decimal.
  const key = `chart:${face}:${Math.round(sizePx * 10) / 10}:${ch.codePointAt(0) ?? 32}`;
  return halo ? key : `${key}:${CHART_BARE}`;
}

/** The font shorthand one face is set in at a size. */
export function chartFont(face: ChartFace, sizePx: number): string {
  return face === 'mono'
    ? `500 ${sizePx}px ${CHART_MONO_FAMILY}`
    : `italic 400 ${sizePx}px ${CHART_FELL_FAMILY}`;
}

/**
 * The scratch context the Fell advances are measured on.
 *
 * It is its own canvas and not the atlas's, because a resolver is a plain
 * function: it has no atlas to ask. jsdom has no 2D backend at all, so the
 * context is null there and every advance takes the fallback em.
 */
let measureCtx: CanvasRenderingContext2D | null | undefined;
function measurer(): CanvasRenderingContext2D | null {
  if (measureCtx === undefined) {
    measureCtx = null;
    if (typeof document !== 'undefined') {
      try {
        measureCtx = document.createElement('canvas').getContext('2d');
      } catch {
        measureCtx = null;
      }
    }
  }
  return measureCtx;
}

/**
 * The advance of one glyph, in px at the baked size.
 *
 * The mono face states one advance for every glyph it carries, so that one
 * is arithmetic. The Fell face is proportional, so it is measured.
 */
export function chartAdvancePx(face: ChartFace, sizePx: number, ch: string): number {
  if (face === 'mono') return sizePx * CHART_MONO_ADVANCE_EM;
  const ctx = measurer();
  if (!ctx) return sizePx * CHART_FALLBACK_ADVANCE_EM;
  ctx.font = chartFont(face, sizePx);
  const w = ctx.measureText(ch).width;
  return w > 0 ? w : sizePx * CHART_FALLBACK_ADVANCE_EM;
}

// ---------------------------------------------------------------------------
// The spec table (the plan's A.7)
// ---------------------------------------------------------------------------

const HULL_SCALE = 4;
const BADGE_SCALE = 4;
const TORPEDO_SCALE = 4;
const SITE_SCALE = 2;
const BLOOM_SCALE = 2;
const GRADIENT_SCALE = 1;

/**
 * The four kraken bodies (R525).
 *
 * A beast is 84 to 152 world px across, and a hull's supersampling would
 * make one cell 600 texels wide. Two is enough for a silhouette a reader
 * mostly meets zoomed out.
 */
const KRAKEN_SCALE = 2;
/** One engraving stroke, in world px. `hatch` of the mockup draws 0.9. */
const KRAKEN_HATCH_PX = 0.9;
/** The soft mass of cards 06 and 07, in world px. The layer scales it. */
const KRAKEN_BLUR_PX = 256;

/**
 * The whale silhouette of the creatures layer (`render/creatures.ts:159-173`):
 * the body, the fluke and a suggestion of a fin, filled in `style`. The
 * atlas bakes it white so the layer tints it, and the Discord emoji
 * `ap_whale` fills it in an ink of its own (brand.md §14.3).
 */
export function drawWhale(ctx: CanvasRenderingContext2D, style: string): void {
  ctx.beginPath();
  ctx.moveTo(-26, 0);
  ctx.quadraticCurveTo(-6, -9, 20, -3);
  ctx.quadraticCurveTo(28, 0, 20, 3);
  ctx.quadraticCurveTo(-6, 9, -26, 0);
  ctx.closePath();
  ctx.fillStyle = style;
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-26, 0);
  ctx.lineTo(-36, -7);
  ctx.lineTo(-32, 0);
  ctx.lineTo(-36, 7);
  ctx.closePath();
  ctx.fill();
}

/** Every loop of a kraken body as one path, in card units. */
function traceKraken(ctx: CanvasRenderingContext2D, b: KrakenBody): void {
  ctx.beginPath();
  for (const loop of b.loops) {
    ctx.moveTo(loop[0], loop[1]);
    for (let i = 2; i < loop.length; i += 2) ctx.lineTo(loop[i], loop[i + 1]);
    ctx.closePath();
  }
}

/**
 * The mass of one kraken body, filled in `style` (R525). The atlas bakes
 * it white so the layer tints it, and the Discord emoji `ap_kraken` fills
 * it in the beast's own ink (brand.md §14.3).
 */
export function drawKrakenMass(ctx: CanvasRenderingContext2D, b: KrakenBody, style: string): void {
  traceKraken(ctx, b);
  ctx.fillStyle = style;
  ctx.fill();
}

/**
 * The engraving inside one kraken body: the strokes of its hatch plan,
 * clipped to its loops, in `style` (R525). The atlas bakes it white, and
 * the Discord emoji `ap_kraken` strokes it in the mark tone.
 */
export function drawKrakenHatch(ctx: CanvasRenderingContext2D, b: KrakenBody, style: string): void {
  const { cx, cy, span, gap, ang } = b.hatch;
  const ca = Math.cos(ang), sa = Math.sin(ang);
  ctx.save();
  traceKraken(ctx, b);
  ctx.clip();
  ctx.lineCap = 'butt';
  ctx.lineWidth = KRAKEN_HATCH_PX;
  ctx.strokeStyle = style;
  ctx.beginPath();
  for (let d = -span; d <= span; d += gap) {
    const mx = cx - sa * d, my = cy + ca * d;
    ctx.moveTo(mx - ca * span, my - sa * span);
    ctx.lineTo(mx + ca * span, my + sa * span);
  }
  ctx.stroke();
  ctx.restore();
}

/** The alpha stops of the soft mass: `blob` of the mockup. */
const KRAKEN_BLUR_STOPS: readonly (readonly [number, number])[] = [[0, 1], [0.55, 0.55], [1, 0]];

/**
 * The soft mass that has no edge at all, one radial gradient over the
 * centred square of `KRAKEN_BLUR_PX`, in the ink `ink` (R525). Card 06
 * opens as this blur and card 07 lies in one. The atlas bakes it white so
 * the layer tints it, and the Discord kraken stickers fill it in the
 * beast's own ink (brand.md §14.3).
 */
export function drawKrakenBlur(ctx: CanvasRenderingContext2D, ink: number): void {
  radial(ctx, KRAKEN_BLUR_PX, KRAKEN_BLUR_STOPS.map(([at, a]) => [at, rgba(ink, a)] as const));
}

/** A radial gradient over a centred square of `size` world px. */
function radial(
  ctx: CanvasRenderingContext2D, size: number,
  stops: readonly (readonly [number, string])[],
  innerR = 0,
): void {
  const half = size / 2;
  const g = ctx.createRadialGradient(0, 0, innerR, 0, 0, half);
  for (const [at, color] of stops) g.addColorStop(at, color);
  ctx.fillStyle = g;
  ctx.fillRect(-half, -half, size, size);
}

/**
 * The banded alpha ramp of one pressure-haze edge, drawn into the cell's own
 * 8 x 256 box (`render/haze.ts:44-60`).
 *
 * The ramp is opaque along y = 0 and gone by the foot, and each of
 * `HAZE_BANDS` bands holds its value before it falls away. The exponent
 * 1.6, the 0.72 shoulder and the two stop nudges are literals of that
 * module (`render/haze.ts:49-56`), so they stay literals here.
 */
function hazeBand(ctx: CanvasRenderingContext2D, alpha: number): void {
  const grad = ctx.createLinearGradient(0, 0, 0, 256);
  for (let b = 0; b <= HAZE_BANDS; b++) {
    const t = b / HAZE_BANDS;
    const a = alpha * (1 - t) ** 1.6;
    grad.addColorStop(Math.min(1, t + 0.0001), HAZE_COLOR + a.toFixed(3) + ')');
    grad.addColorStop(Math.min(1, t + 1 / HAZE_BANDS - 0.02), HAZE_COLOR + (a * 0.72).toFixed(3) + ')');
  }
  grad.addColorStop(1, HAZE_COLOR + '0)');
  ctx.fillStyle = grad;
  ctx.fillRect(-4, 0, 8, 256);
}

const centredBox = (w: number, h: number) => ({ x: -w / 2, y: -h / 2, w, h });

/** Traces the map's own hex, corner for corner of `world.hexCorner`. */
function mapHexPath(ctx: CanvasRenderingContext2D): void {
  ctx.beginPath();
  for (let k = 0; k < 6; k++) {
    const [x, y] = hexCorner(k);
    if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** A cell that is ONE radial gradient over a centred square (`radial`). */
function radialSpec(
  key: string, size: number,
  stops: readonly (readonly [number, string])[], innerR = 0,
): AtlasSpec {
  return {
    key,
    scale: GRADIENT_SCALE,
    box: centredBox(size, size),
    draw: (ctx) => radial(ctx, size, stops, innerR),
  };
}

/** How wide a glint's arm is at the waist, as a fraction of the cell. */
const EFFECT_GLINT_WAIST = 0.06;

/** One hull master, filled or stroked, by the recipe of `fleets.ts:407-419`. */
function hullSpec(
  style: ShipStyle, cls: number, path: HullPath, hollow: boolean, cockpit: boolean,
): AtlasSpec {
  return {
    key: hullKey(style, cls, hollow),
    scale: HULL_SCALE,
    draw(ctx) {
      const m = new Canvas2DMarks(ctx);
      path(m);
      if (hollow) m.stroke({ width: FLEET_HOLLOW_STROKE_PX, color: 0xffffff, join: 'round' }); // mask
      else m.fill({ color: 0xffffff }); // mask
      // The dart's cockpit dot rides on the filled master only.
      if (cockpit && !hollow) m.circle(4, 0, 2.4).fill({ color: 0xffffff, alpha: 0.9 }); // mask
    },
  };
}

/**
 * One NPC mark (R2637): the geometry of `render/npc-marks.ts`, scaled from
 * its box to `FLEET_NPC_MARK_PX` round the origin, bow east. The light
 * parts bake white, so the layer tints them in the NPC ink; the deep parts
 * bake in the deep ink, which a tint keeps dark. The swallowtail is the
 * inverse hull of `paintPirateHull` with no class and no brotherhood
 * (R2874): the deep body with the light rim, as the legend row draws it.
 * The ring of a monster keeps its deep core at `NPC_INK.edgeAlpha`, the
 * `--npc` line with the core `--npc-edge`.
 */
function npcSpec(kind: NpcKind): AtlasSpec {
  if (kind !== NpcKind.NPC_KIND_MONSTER) {
    return {
      key: npcKey(kind),
      scale: HULL_SCALE,
      draw: (ctx) => paintPirateHull(ctx, ShipClass.SHIP_CLASS_UNSPECIFIED, 0, PIRATE_INKS),
    };
  }
  const k = FLEET_NPC_MARK_PX / NPC_BOX;
  const c = NPC_BOX / 2;
  const edge = rgba(NPC_INK.edge, NPC_INK.edgeAlpha);
  return {
    key: npcKey(kind),
    scale: HULL_SCALE,
    draw(ctx) {
      ctx.scale(k, k);
      ctx.translate(-c, -c);
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      for (const a of MONSTER.arms) {
        ctx.moveTo(a.x0, a.y0);
        ctx.quadraticCurveTo(a.cx, a.cy, a.x1, a.y1);
      }
      ctx.strokeStyle = '#ffffff'; // mask
      ctx.lineWidth = NPC_STROKE.line;
      ctx.stroke();
      ctx.beginPath();
      MONSTER.ring.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.closePath();
      ctx.fillStyle = edge;
      ctx.fill();
      ctx.lineWidth = NPC_STROKE.ring;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(MONSTER.eye.x, MONSTER.eye.y, MONSTER.eye.r, 0, TAU);
      ctx.fillStyle = '#ffffff'; // mask
      ctx.fill();
    },
  };
}

/** The two inks of a pirate cell: the light parts (the rim, the eye, the
 *  gear) bake white, so the layer tints them in the NPC ink, and the body
 *  bakes in the deep ink, which a tint keeps dark (R2637, R2874). */
const PIRATE_INKS: PirateInks = {
  light: (a) => `rgba(255,255,255,${a})`, // mask
  deep: (a) => rgba(NPC_INK.edge, a),
};

/**
 * One pirate cell of round 113 (R2720), baked on demand: a hull of
 * `npc:pirate:<cls>:<bro>` or a light of `npc:light:<light>`. A key that
 * names a class with no hull mark or a brotherhood past the five answers
 * null.
 */
export function pirateSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p[0] !== 'npc') return null;
  if (p[1] === 'light' && p.length === 3 && (p[2] === 'lantern' || p[2] === 'knot')) {
    const light = p[2];
    return { key, scale: HULL_SCALE, draw: (ctx) => paintPirateLight(ctx, light, PIRATE_INKS) };
  }
  if (p[1] !== 'pirate' || p.length !== 4) return null;
  const cls = Number(p[2]), bro = Number(p[3]);
  if (!Number.isInteger(cls) || !(cls === 0 || HULL_FIELD_OF_CLASS.has(cls))) return null;
  if (!Number.isInteger(bro) || bro < 0 || bro > PIRATE_BROTHERHOODS) return null;
  return { key, scale: HULL_SCALE, draw: (ctx) => paintPirateHull(ctx, cls, bro, PIRATE_INKS) };
}

/** The brotherhoods of `[[npc.brotherhoods]]`: the wire value runs 1 to 5. */
const PIRATE_BROTHERHOODS = Brotherhood.BROTHERHOOD_UNDERTOW;

/**
 * The eager specs: everything that does not depend on a faction, a resource
 * or a roster flag. The site marks are resolved on demand instead, because a
 * base alone has twenty variants and a world shows a handful.
 */
export function buildAtlasSpecs(): AtlasSpec[] {
  const specs: AtlasSpec[] = [];

  // -- hulls, filled and hollow (the plan's A.7 row 1) -----------------------
  // The AUTO set only (R835). The other eleven bake through `hullSpecFor`.
  for (const hollow of [false, true]) {
    specs.push(hullSpec(
      DEFAULT_SHIP_STYLE, ShipClass.SHIP_CLASS_UNSPECIFIED, dartPath, hollow, true,
    ));
    for (const [cls, path] of [...SUPPORT_ICONS, ...COMBAT_ICONS]) {
      specs.push(hullSpec(DEFAULT_SHIP_STYLE, cls, path, hollow, false));
    }
  }
  // -- the two NPC marks (v2-the-deep.md §8, R2637) --------------------------
  specs.push(npcSpec(NpcKind.NPC_KIND_PIRATE), npcSpec(NpcKind.NPC_KIND_MONSTER));

  // -- the hull bloom (`fleets.ts:439-445`) ---------------------------------
  specs.push({
    key: 'bloom',
    scale: BLOOM_SCALE,
    box: centredBox(FLEET_BLOOM_HALF_LEN * 2, FLEET_BLOOM_HALF_W * 2),
    draw(ctx) {
      const layers = 6;
      for (let i = 0; i < layers; i++) {
        const k = 1 - i / layers;
        ctx.beginPath();
        ctx.ellipse(0, 0, FLEET_BLOOM_HALF_LEN * k, FLEET_BLOOM_HALF_W * k, 0, 0, TAU);
        ctx.fillStyle = rgba(0xffffff, 0.07); // mask
        ctx.fill();
      }
    },
  });

  // -- the control-group badges (`fleets.ts:473-517`, R427) -----------------
  // The AUTO value `mono` alone is eager, because it is the badge nearly
  // every page draws. The four drawn alphabets of R833 bake on demand,
  // through `badgeSpecFor` below.
  for (let n = 1; n <= BADGE_DIGITS; n++) {
    specs.push({
      key: badgeKey('mono', n),
      scale: BADGE_SCALE,
      box: centredBox(BADGE_W, BADGE_FONT_PX),
      draw(ctx) {
        ctx.font = badgeFont();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#ffffff'; // mask
        ctx.fillText(String(n), 0, 0);
      },
    });
  }

  // -- the damage figure's glyphs and darts (R538) --------------------------
  // One cell a glyph a weight, on a FIXED box, so the digits of one figure
  // share a baseline and the layer places them by the advance alone. The
  // box is one halo wider and taller than the advance, so the stroke is
  // never cut at the crop bound.
  for (const weight of DAMAGE_WEIGHTS) {
    for (const ch of DAMAGE_GLYPHS) specs.push(damageGlyphSpec(damageGlyphKey(weight, ch), weight, ch, null));
  }
  // The two marks of the aspect (R2407, §6.3.2): the chevron of the suffix
  // and the hollow diamond of an ambush, at the gun size, with the halo of
  // a glyph under the white ink.
  for (const key of [DAMAGE_CHEV_KEY, DAMAGE_DIAMOND_KEY]) specs.push(damageMarkSpec(key, null));
  for (const hollow of [false, true]) {
    specs.push(damageDartSpec(hollow ? DAMAGE_DART_HOLLOW_KEY : DAMAGE_DART_KEY, hollow, null));
  }

  // -- the rounds in flight (`torpedo-styles.ts` `paint`) -------------------
  for (const key of Object.keys(TORPEDO_SHAPE) as TexKey[]) {
    const [w, h] = TORPEDO_SHAPE[key];
    specs.push({
      key: torpedoKey(key),
      scale: TORPEDO_SCALE,
      box: centredBox(w, h),
      draw(ctx) {
        // `paint` draws in [0,w] x [0,h] from a top-left origin.
        ctx.save();
        ctx.translate(-w / 2, -h / 2);
        paintTorpedo(key, ctx, w, h);
        ctx.restore();
      },
    });
  }

  // -- the radial dots (`wake.ts:153-161`, `control.ts:214-224`,
  //    `water.ts:102-108` and `:131-137`) ----------------------------------
  specs.push(radialSpec('wake', 32, [
    [0, 'rgba(255,255,255,1)'], // mask
    [0.35, 'rgba(255,255,255,0.45)'], // mask
    [1, 'rgba(255,255,255,0)'], // mask
  ]));
  specs.push(radialSpec('pip', CONTROL_PIP.texSize,
    CONTROL_PIP.stops.map(([at, a]) => [at, `rgba(255,255,255,${a})`] as const))); // mask
  // The fog and the snow cell are dark-only (R3296): the light identity
  // draws no fog, and its snow is the `silt` mask in `SNOW_COLOR`.
  specs.push(radialSpec('fog', 256, [
    [0, 'rgba(70,130,160,0.16)'], // mask: dark only (R3296)
    [1, 'rgba(70,130,160,0)'], // mask: dark only (R3296)
  ], 10));
  specs.push(radialSpec('snow', 8, [
    [0, 'rgba(190,220,235,0.85)'], // mask: dark only (R3296)
    [1, 'rgba(190,220,235,0)'], // mask: dark only (R3296)
  ]));
  // The seaquake's silt grain (`render/quake.ts:174-182`).
  specs.push(radialSpec('silt', 16, [
    [0, 'rgba(255,255,255,0.95)'], // mask
    [1, 'rgba(255,255,255,0)'], // mask
  ]));

  // -- the pressure haze bands (`render/haze.ts:44-70`) ---------------------
  // Two cells, one for each alpha. `PressureHaze` stretches one sprite of a
  // cell along each world edge, so the cell keeps the source box: 8 px wide,
  // 256 px deep, with the origin on the opaque line at its head.
  for (const [key, alpha] of [
    ['haze-near', HAZE_NEAR_ALPHA],
    ['haze-far', HAZE_FAR_ALPHA],
  ] as const) {
    specs.push({
      key,
      scale: GRADIENT_SCALE,
      box: { x: -4, y: 0, w: 8, h: 256 },
      draw: (ctx) => hazeBand(ctx, alpha),
    });
  }

  // -- the whale silhouette (`render/creatures.ts:159-173`) -----------------
  // Body, fluke and a suggestion of a fin, white so the layer tints it. It
  // carries no box, so the baker crops to the ink exactly as
  // `renderer.generateTexture` cropped the PixiJS master.
  specs.push({
    key: 'whale',
    scale: HULL_SCALE,
    draw(ctx) {
      drawWhale(ctx, '#ffffff'); // mask
    },
  });

  // -- the four beasts of the old charts (R525) -----------------------------
  // Two cells a body: the mass, and the engraving inside it. Both are
  // measured by the SAME box, so the layer draws them origin-relative and
  // the hatching sits on the mantle it belongs to. The mass is white, so
  // the layer tints it with the beast's ink; the hatching is white as well
  // and takes the pale mark tone.
  //
  // The rim is NOT baked. One cell takes one tint and one alpha, and the
  // sonar must run its light AROUND the edge, so the layer strokes the same
  // loops as capsules and reads the front at each one (R525, R101).
  for (const b of KRAKEN_BODIES) {
    specs.push({
      key: `kraken-${b.key}`,
      scale: KRAKEN_SCALE,
      box: b.box,
      draw(ctx) {
        drawKrakenMass(ctx, b, '#ffffff'); // mask
      },
    });
    specs.push({
      key: `kraken-${b.key}-hatch`,
      scale: KRAKEN_SCALE,
      box: b.box,
      draw(ctx) {
        drawKrakenHatch(ctx, b, '#ffffff'); // mask
      },
    });
  }
  // The mass that has no edge at all: card 06 opens as a blur of r 92 and
  // card 07 lies in one. `blob` of the mockup, as a cell the layer tints
  // dark and scales — the ambience `fog` cell bakes its own 0.16 peak and
  // would leave the beast invisible. `drawKrakenBlur` paints it, so the
  // Discord kraken stickers draw the same gradient.
  specs.push({
    key: 'kraken-blur',
    scale: GRADIENT_SCALE,
    box: centredBox(KRAKEN_BLUR_PX, KRAKEN_BLUR_PX),
    draw: (ctx) => drawKrakenBlur(ctx, 0xffffff), // mask
  });

  // -- the arrival's two baked cells (`render/spawn.ts:229-276`) ------------
  // Caustic Sweep's ribbons: seven wobbling polylines in a 256 x 512 sheet.
  // Ribbon 0 is the brightest and sits on the right edge, which the sprite
  // anchors on the front — so the box puts the origin on that edge and the
  // sheet hangs to the left of it. The wobble figures and the 0.55 head
  // alpha are literals of that module (`render/spawn.ts:236-249`); the
  // bright ink of ribbon 0 is `mark_inks.caustic` (R3296). A ribbon that leaves the 256 px width is
  // cropped by the box, as it was by the source canvas.
  specs.push({
    key: 'spawn-caustic',
    scale: GRADIENT_SCALE,
    box: { x: -256, y: -256, w: 256, h: 512 },
    draw(ctx) {
      ctx.save();
      ctx.translate(-256, -256);
      ctx.lineCap = 'round';
      for (let i = 0; i < 7; i++) {
        const x0 = 250 - i * 30;
        const alpha = 0.55 * (1 - i / 7);
        ctx.strokeStyle = i === 0 ? rgba(MARK_INKS.caustic, alpha) : rgba(ACCENT_COLOR, alpha);
        ctx.lineWidth = i === 0 ? 3 : 1.7;
        ctx.beginPath();
        for (let y = -8; y <= 520; y += 8) {
          const x = x0 + Math.sin(y * 0.035 + i * 1.4) * 9;
          if (y <= -8) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      ctx.restore();
    },
  });

  // Cell Ripple's veil cell: a filled hex with a brighter rim. The body is
  // grey and the rim is white, so one tint gives a dark cell AND a visible
  // edge (`render/spawn.ts:254-276`). The radius is 64 with a 4 px pad,
  // which is the 136 px box, and the orientation is the map's own.
  specs.push({
    key: 'spawn-hex',
    scale: GRADIENT_SCALE,
    box: centredBox(136, 136),
    draw(ctx) {
      ctx.beginPath();
      hexPath(ctx, 0, 0, 64);
      ctx.fillStyle = '#9e9e9e'; // mask
      ctx.fill();
      ctx.strokeStyle = '#ffffff'; // mask
      ctx.lineWidth = 5;
      ctx.stroke();
    },
  });

  // -- one flat hex at the map's own size, for the hex-fill layer ----------
  // The corners come from `world.hexCorner`, so the cell IS the map's hex
  // and not a copy of its shape. It carries no box: the crop keeps one
  // transparent texel a side, which gives the fill an antialiased edge and
  // still tiles, because the pad is the same on the two opposite sides.
  specs.push({
    key: 'hex',
    scale: HULL_SCALE,
    draw(ctx) {
      mapHexPath(ctx);
      ctx.fillStyle = '#ffffff'; // mask
      ctx.fill();
    },
  });

  // -- the same hex STROKED, for the hex-cells shield aura (R831) ----------
  // The aura draws the fill and the edge of a cell as two marks, because
  // one batch instance carries one alpha and the ripple moves the two
  // alphas apart. The stroke is the figure of the aura's own table.
  specs.push({
    key: 'hexrim',
    scale: HULL_SCALE,
    draw(ctx) {
      mapHexPath(ctx);
      ctx.lineJoin = 'round';
      ctx.lineWidth = FLEET_AURA.hexCells.edgeW;
      ctx.strokeStyle = '#ffffff'; // mask
      ctx.stroke();
    },
  });

  // -- the map author's marks (`marks.ts:430-441`) --------------------------
  // `render/markers.ts` draws these two through the same functions. The
  // cells are baked white, so the marker layer tints each one with the
  // author's own colour.
  specs.push({
    key: 'marker-arrow',
    scale: SITE_SCALE,
    draw: (ctx) => drawMarkerArrow(new Canvas2DMarks(ctx), 0xffffff), // mask
  });
  specs.push({
    key: 'marker-circle',
    scale: SITE_SCALE,
    draw: (ctx) => drawMarkerCircle(new Canvas2DMarks(ctx), 0xffffff), // mask
  });

  // -- the captain's chevron (`fleets.ts:1228`) -----------------------------
  // Drawn white in its own place above the hull, so the entry's `ox`/`oy`
  // carry the offset and the layer tints it gold.
  specs.push({
    key: 'chevron',
    scale: HULL_SCALE,
    draw(ctx) {
      ctx.beginPath();
      ctx.moveTo(-4, -12);
      ctx.lineTo(0, -17);
      ctx.lineTo(4, -12);
      ctx.lineTo(0, -14);
      ctx.closePath();
      ctx.fillStyle = '#ffffff'; // mask
      ctx.fill();
    },
  });

  // -- the aspect chevrons (R2407, §6.1.11) ---------------------------------
  // The rest set in four cells, one for each gap a flash opens, on a square
  // box centred on the hull origin, so the quad turns with the bow about
  // the hull. One chevron at the rest stroke and one at the flash stroke,
  // each centred on its tip. All bake white and take the tint.
  const setHalf = chevronSetHalf();
  for (const gap of CHEVRON_GAPS) {
    specs.push({
      key: chevronSetKey(gap),
      scale: HULL_SCALE,
      box: centredBox(setHalf * 2, setHalf * 2),
      draw: (ctx) => drawChevronSet(ctx, gap),
    });
  }
  for (const [key, width] of [
    [CHEVRON_KEY, FLEET_ASPECT.width], [CHEVRON_FLASH_KEY, FLEET_ASPECT.flashWidth],
  ] as const) {
    const half = chevronHalf(width);
    specs.push({
      key,
      scale: HULL_SCALE,
      box: centredBox(half * 2, half * 2),
      draw: (ctx) => drawOneChevron(ctx, width),
    });
  }

  // -- the flat white texel, for a plain rectangle ------------------------
  // The readout of the fleet layer used it. That layer draws its bars as
  // butt-capped segments now, because a segment carries its own antialiased
  // edge and a flat quad carries none. The cell stays for a layer that
  // wants one solid texel at any size.
  specs.push({
    key: 'white',
    scale: GRADIENT_SCALE,
    box: centredBox(4, 4),
    collapse: true,
    draw(ctx) {
      ctx.fillStyle = '#ffffff'; // mask
      ctx.fillRect(-2, -2, 4, 4);
    },
  });

  return specs;
}

/** The four ranks a hold key can name (R633). */
const HOLD_RANKS: readonly HoldRank[] = ['own', 'mate', 'faction', 'other'];
/** The two readings a key can name. */
const HOLD_ERAS: readonly HoldEra[] = ['h', 'm'];

/** One part of a key as a member of its list, or `null` for a part the list lacks. */
/**
 * The part of a hold key that names the mark, for that rank (R834).
 *
 * The own rank takes a value of `ownHold`, the mate rank a value of
 * `squadHold`, and the two plain ranks take `-`, which is no mark. Anything
 * else is `null`, which the resolver reads as a miss.
 */
function holdMark(
  s: string, rank: HoldRank,
): OwnHoldStyle | SquadHoldStyle | '-' | null {
  if (rank === 'own') return isOwnHoldMark(s) ? s : null;
  if (rank === 'mate') return isSquadHoldMark(s) ? s : null;
  return s === '-' ? '-' : null;
}

/**
 * The badge of card 01 of round 075 beside a hold (R1568), in the faction's
 * charge and ink, at the place `factionmarks.toml` gives for that reading.
 * It bakes into the cell of the mark.
 */
function siteBadge(m: MarkGraphics, era: HoldEra, faction: number): void {
  const f = factionOf(faction);
  if (!f) return;
  const [dx, dy] = siteBadgeAt(era);
  drawFactionBadge(m, era, f.charge, f.color, dx, dy, FACTION_MARKS.site.sizePx);
}

/**
 * The charge pip astern of a hull of another faction (R1568, card 02 of
 * round 075): `fpip:<reading>:<faction>`. The cell is baked in its own two
 * inks, the panel ground and the faction ink, at the hull's supersampling,
 * and centred on its origin, so the quad stands upright on the pip's place.
 */
export function factionPipSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p.length !== 3 || p[0] !== 'fpip') return null;
  const era = oneOf(HOLD_ERAS, p[1]) ?? null;
  const f = factionOf(Number(p[2]));
  if (!era || !f) return null;
  return {
    key,
    scale: HULL_SCALE,
    draw: (ctx) => drawFactionBadge(
      new Canvas2DMarks(ctx), era, f.charge, f.color, 0, 0, FACTION_MARKS.hull.sizePx),
  };
}

/**
 * Resolves a site key into its spec, so the atlas bakes a site mark the
 * first time a world shows one. The key scheme mirrors `sites.ts:74-100`.
 */
export function siteSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p[0] !== 'site') return null;
  const n = (i: number) => Number(p[i]);
  const spec = (draw: (m: Canvas2DMarks) => void): AtlasSpec => ({
    key,
    scale: SITE_SCALE,
    draw: (ctx) => draw(new Canvas2DMarks(ctx)),
  });
  switch (p[1]) {
    case 'hold': {
      const symbol = oneOf(HOLD_SYMBOL_IDS, p[2]) ?? null;
      const era = oneOf(HOLD_ERAS, p[3]) ?? null;
      const rank = oneOf(HOLD_RANKS, p[5]) ?? null;
      if (!symbol || !era || !rank) return null;
      // The mark must be on the rank's OWN list (R834). A key that names a
      // squad mark at the own rank, or any mark at a plain rank, is a miss
      // and not a mark drawn from a default nobody asked for.
      const mark = holdMark(p[6], rank);
      if (mark === null) return null;
      // The charge of card 01 (R1568) stands beside a hold of another
      // faction alone; any other rank that names it is a miss.
      const withCharge = p[7] === 'c';
      if (p.length > 8 || (p.length === 8 && (!withCharge || rank !== 'other'))) return null;
      return spec((m) => {
        drawHoldMark(m, symbol, era, factionColor(n(4)), rank, mark === '-' ? undefined : mark);
        if (withCharge) siteBadge(m, era, n(4));
      });
    }
    case 'holdanim': {
      // One cell of a hold mark that moves (R834). It is baked white at the
      // site's own supersampling, because the quad carries the tint and the
      // alpha the card's own function writes every frame.
      const part = HOLD_ANIM_PARTS.get(key);
      if (!part) return null;
      return spec((m) => part.draw(m));
    }
    case 'holdlight':
    case 'holdruin': {
      const symbol = oneOf(HOLD_SYMBOL_IDS, p[2]) ?? null;
      const era = oneOf(HOLD_ERAS, p[3]) ?? null;
      if (!symbol || !era) return null;
      const draw = p[1] === 'holdlight' ? drawHoldLight : drawHoldRuin;
      return spec((m) => draw(m, symbol, era));
    }
    case 'inst': {
      // `site:inst:<era>:<faction>:<own>`. Every part is validated, as the
      // `cp` case does: a key the build does not carry is a miss (R2239).
      if (p.length !== 5) return null;
      const era = oneOf(HOLD_ERAS, p[2]) ?? null;
      if (!era) return null;
      const faction = n(3);
      if (!Number.isInteger(faction) || faction < 0) return null;
      if (p[4] !== '0' && p[4] !== '1') return null;
      const own = p[4] === '1';
      return spec((m) => drawInstallationMark(m, era, factionColor(faction), own));
    }
    case 'instruin': {
      const era = oneOf(HOLD_ERAS, p[2]) ?? null;
      if (!era) return null;
      return spec((m) => drawInstallationRuin(m, era));
    }
    case 'deposit': {
      // The fill step is part of the key, because the drain is geometry and
      // the atlas bakes geometry one time for each shape (R475), and so is
      // the value of the `deposit` field (R832).
      const style = oneOf<DepositStyle>(DEPOSIT_STYLES, p[2]) ?? null;
      if (!style) return null;
      const res = n(3), level = n(4);
      const ink = depositInk(res);
      const tag = DEPOSIT_MARKS.tag;
      return {
        key,
        scale: SITE_SCALE,
        draw(ctx) {
          // The halo of card 06 is a radial gradient, which a mark
          // description cannot ask for, so the cell carries it UNDER the
          // cluster the description draws.
          if (style === 'ghost' && level > 0) {
            const g = DEPOSIT_MARKS.ghost;
            radial(ctx, g.haloR * 2, [
              [0, rgba(ink, g.haloAlpha)], [1, rgba(ink, 0)],
            ]);
          }
          drawDepositStyle(new Canvas2DMarks(ctx), style, res, level);
          // The letter of card 08 is text, so the cell carries it OVER the
          // tile. The face is the vendored chart mono of R548; a cell baked
          // before the file lands is re-drawn in `loadChartFaces`.
          if (style === 'tag') {
            ctx.font = chartFont('mono', tag.fontPx);
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = level > 0
              ? rgba(tag.ink, 1) : rgba(ink, tag.emptyAlpha);
            ctx.fillText(depositLetter(res), 0, tag.letterDy);
          }
        },
      };
    }
    case 'cp': {
      // `site:cp:<set>:<kind>:<faction>:<attacker>` (R1104). Every part is
      // validated, the way `holdMark` refuses a bad part: a key the build
      // does not carry is a miss, and not a mark drawn from a default
      // nobody asked for.
      if (p.length !== 6) return null;
      const set = oneOf(STRUCTURE_SYMBOL_IDS, p[2]) ?? null;
      if (!set) return null;
      const kindNo = n(3), faction = n(4), attacker = n(5);
      if (!Number.isInteger(kindNo) || !Number.isInteger(faction)
        || !Number.isInteger(attacker) || faction < 0 || attacker < 0) return null;
      const kind = structureKindOf(kindNo);
      // A control point with no owner wears the neutral grey (`sites.ts`),
      // which is what `factionColor(0)` answers.
      const own = factionColor(faction);
      // The unspecified kind is the state before the first Sieges frame. It
      // draws the unkeyed mark of today, and the layer re-keys the site when
      // the frame lands.
      if (kind === null) {
        if (kindNo !== 0) return null;
        return spec((m) => drawCP(m, own));
      }
      return spec((m) => drawStructureMark(m, set, kind, {
        own, foe: attacker === 0 ? 0 : factionColor(attacker), held: faction !== 0,
      }));
    }
    case 'flag': {
      // `site:flag`, the objective tell of an arcade structure (R2029). It
      // is baked WHITE at its path origin, the centre of the structure, and
      // the quad carries the owner ink.
      if (key !== OBJECTIVE_FLAG_KEY) return null;
      return { key, scale: SITE_SCALE, draw: (ctx) => paintObjectiveFlag(ctx, 0, 0, '#ffffff') }; // mask
    }
    default:
      return null;
  }
}

/**
 * Resolves a chart glyph key into its spec, so the atlas bakes a glyph the
 * first time a piece writes it (R548).
 *
 * The cell has a FIXED box, as the badge and the damage glyphs do, so every
 * glyph of one face and size sits on one baseline. The box is the advance
 * plus a halo on each side, and the height is `CHART_LINE_EM` ems plus the
 * same halo, so the stroke is never cut at the crop bound.
 *
 * A key with the fifth part `bare` bakes the same cell with no halo stroke
 * (R1439).
 *
 * The box is centred on the origin, so the entry's own centre IS the glyph
 * centre `render/text-run.ts` lays out. `base` says where the baseline sits
 * under the cell top, for a piece that aligns a run by its baseline.
 */
export function chartSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p[0] !== 'chart') return null;
  if (p.length !== 4 && !(p.length === 5 && p[4] === CHART_BARE)) return null;
  return chartGlyphSpec(key, key, null);
}

/**
 * One chart glyph cell of the key `base` (R548), under the atlas key `key`:
 * in the mask, or baked in `ink` over the paper halo (R3296). An inked cell
 * of a bare key bakes no halo, as its mask does.
 */
function chartGlyphSpec(key: string, base: string, ink: number | null): AtlasSpec | null {
  const p = base.split(':');
  if (p[0] !== 'chart') return null;
  if (p.length !== 4 && !(p.length === 5 && p[4] === CHART_BARE)) return null;
  const bare = p.length === 5;
  const face = oneOf(CHART_FACES, p[1]) ?? null;
  if (!face) return null;
  const size = Number(p[2]);
  const cp = Number(p[3]);
  if (!Number.isFinite(size) || size <= 0) return null;
  if (!Number.isInteger(cp) || cp < 0 || cp > 0x10ffff) return null;
  const ch = String.fromCodePoint(cp);
  const halo = CHART_HALO_PX;
  const adv = chartAdvancePx(face, size, ch);
  const w = Math.ceil(adv + halo * 2);
  const h = Math.ceil(size * CHART_LINE_EM + halo * 2);
  const baseY = halo + size * CHART_ASCENT_EM;
  const font = chartFont(face, size);
  return {
    key,
    scale: CHART_SCALE,
    adv,
    base: baseY,
    box: centredBox(w, h),
    draw(ctx) {
      ctx.font = font;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      // A bare cell keeps the box of its haloed twin, so the glyph stands
      // at the same place, and strokes no halo.
      if (!bare && (GLYPH_HALO || ink !== null)) {
        ctx.lineJoin = 'round';
        ctx.lineWidth = halo;
        ctx.strokeStyle = rgba(DAMAGE_HALO_INK, CHART_HALO_ALPHA);
        ctx.strokeText(ch, -w / 2 + halo, -h / 2 + baseY);
      }
      ctx.fillStyle = glyphFill(ink);
      ctx.fillText(ch, -w / 2 + halo, -h / 2 + baseY);
    },
  };
}

/** The filled or stroked chart marks a batch cannot draw (R1248). */
export type ChartMarkCell = 'star' | 'leaf-fill' | 'leaf-line';

/** The atlas key of one chart mark. It is not `chart:`, which is a glyph. */
export function chartMarkKey(cell: ChartMarkCell): string {
  return `chartmark:${cell}`;
}

/** The supersampling of the chart marks: a cell a few world px wide. */
const CHART_MARK_SCALE = 8;

/**
 * One chart mark cell (R1248): `chartmark:<cell>`.
 *
 * Mockup round 059 fills a star (card 10) and fills or strokes a laurel
 * leaf (card 18). The segment and circle batches draw neither a polygon
 * fill nor an ellipse, so the cell is baked WHITE at the size the pack
 * states and the quad carries the ink. The origin is the shape's centre:
 * the star's centre is not its box centre, so a piece draws the cell with
 * `centred = false`.
 */
export function chartMarkSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p.length !== 2 || p[0] !== 'chartmark') return null;
  const hold = CHART.pieces.hold_site.fig;
  const pool = CHART.pieces.pool_monument;
  switch (p[1]) {
    case 'star':
      return {
        key,
        scale: CHART_MARK_SCALE,
        draw(ctx) {
          ctx.beginPath();
          for (let k = 0; k < 10; k++) {
            const a = -Math.PI / 2 + (k * Math.PI) / 5;
            const d = k % 2 ? hold.star_r_inner : hold.star_r;
            if (k === 0) ctx.moveTo(Math.cos(a) * d, Math.sin(a) * d);
            else ctx.lineTo(Math.cos(a) * d, Math.sin(a) * d);
          }
          ctx.closePath();
          ctx.fillStyle = '#ffffff'; // mask
          ctx.fill();
        },
      };
    case 'leaf-fill':
    case 'leaf-line':
      return {
        key,
        scale: CHART_MARK_SCALE,
        draw(ctx) {
          ctx.beginPath();
          ctx.ellipse(0, 0, pool.fig.leaf_rx, pool.fig.leaf_ry, 0, 0, Math.PI * 2);
          if (p[1] === 'leaf-fill') {
            ctx.fillStyle = '#ffffff'; // mask
            ctx.fill();
          } else {
            ctx.lineWidth = pool.modern.fig.leaf_width;
            ctx.strokeStyle = '#ffffff'; // mask
            ctx.stroke();
          }
        },
      };
    default:
      return null;
  }
}

/**
 * The four inks of the map marker looks (R1291): the white of a squad mark,
 * the water black of §2 (the halo ink), the chrome accent and the pale
 * pencil, which is the chrome text weight. The marker menu paints with the
 * same four, so a target and a marker are one picture.
 */
export const MARKER_INKS: MarkerInks = {
  white: MARK_INKS.white,
  ink: DAMAGE_HALO_INK,
  accent: ACCENT_COLOR,
  pale: MAP_PALETTE.text,
};

/** The supersampling of a marker cell: a mark some twenty world px tall. */
const MARKER_SCALE = 8;

/**
 * One map marker cell (R1291, §17.3.66): `mmark:<cell>[:<kind>]:<audience>`.
 *
 * `render/map-marker-cells.ts` paints it in its own inks, the way the cards
 * of round 062 drew it, so a quad draws it with a WHITE tint and carries
 * the fade alone. The column is the one white cell: the quad tints it with
 * the accent and stretches it to the height of the moment.
 */
export function mapMarkerSpecFor(key: string): AtlasSpec | null {
  const k = readMarkerCellKey(key);
  if (!k) return null;
  const fig = CHART.pieces.map_markers.fig;
  return {
    key,
    scale: MARKER_SCALE,
    draw(ctx) { paintMarkerCell(ctx, k.cell, k.kind, k.aud, fig, MARKER_INKS); },
  };
}

/**
 * The dart of a drill spot (arcade.md §17.8, R1573): `drill-dart`.
 *
 * It is baked WHITE at the edge arrow's own size, with one px of pad a
 * side, because the layer tints each quad with the neutral marker ink and
 * stretches it to the arrowhead or the edge arrow. It is resolved on
 * demand, so a page that plays no drill bakes nothing.
 */
export function drillSpecFor(key: string): AtlasSpec | null {
  if (key !== DRILL_DART_KEY) return null;
  return {
    key,
    scale: SITE_SCALE,
    box: centredBox(DART_CELL_LEN + 2, DART_CELL_W + 2),
    draw: paintDrillDart,
  };
}

/**
 * The commission mark (v2-the-deep.md §14.6, R2645): `commission-mark`,
 * baked WHITE with the foot of the staff at the origin, because the marker
 * layer tints each quad with the own faction ink. It is resolved on demand,
 * so a world with no commission bakes nothing.
 */
export function commissionSpecFor(key: string): AtlasSpec | null {
  if (key !== COMMISSION_MARK_KEY) return null;
  return { key, scale: SITE_SCALE, box: commissionBox(), draw: paintCommissionMark };
}

/**
 * One friendly mark cell (R185, R830): `fmark:<style>:<part>`.
 *
 * The cell is baked WHITE at the hull's own supersampling, because the quad
 * carries the tint: one cell therefore serves every faction. It is resolved
 * on demand, so a page bakes only the value the reader's Marks theme picked
 * and the shipping look bakes nothing at all.
 */
export function fleetMarkSpecFor(key: string): AtlasSpec | null {
  if (!key.startsWith('fmark:')) return null;
  const cell = FLEET_MARK_CELLS.get(key);
  if (!cell) return null;
  return {
    key,
    scale: HULL_SCALE,
    draw: (ctx) => cell.draw(new Canvas2DMarks(ctx)),
  };
}

/**
 * One drawn group digit (R833): `badge:<value>:<n>`.
 *
 * The four seven-segment values of the `digits` field resolve here, and the
 * AUTO value `mono` does not: its five cells are set in the vendored face
 * and they are baked eagerly above.
 *
 * The cell takes a FIXED box, as the `mono` cell does, because the five
 * digits must share one box or five badges would not sit on one axis. The
 * box is the alphabet's own box plus one texel of pad on each side: the ink
 * of a digit reaches its box exactly, and a fixed box takes no `INK_PAD` of
 * its own, so without the pad the bilinear tap would find full alpha on the
 * edge of the quad.
 */
export function badgeSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p.length !== 3 || p[0] !== 'badge') return null;
  const def = Object.hasOwn(DIGIT_STYLE_DEFS, p[1])
    ? DIGIT_STYLE_DEFS[p[1] as DigitStyle] : undefined;
  if (!def || def.kind !== 'seg') return null;
  const n = Number(p[2]);
  if (!Number.isInteger(n) || n < 1 || n > BADGE_DIGITS) return null;
  const pad = INK_PAD / BADGE_SCALE;
  return {
    key,
    scale: BADGE_SCALE,
    box: centredBox(
      segBoxWidth(FLEET_GROUP_BADGE_H) + pad * 2, FLEET_GROUP_BADGE_H + pad * 2,
    ),
    draw: (ctx) => {
      segDigit(new Canvas2DMarks(ctx), n as DigitValue, FLEET_GROUP_BADGE_H, def);
    },
  };
}

/**
 * The two baked shield auras (R831, §6.1): `aura:bubble:<R>` and
 * `aura:mesh:<R>`.
 *
 * Each one is a whole sheet the size of the reach, so it is baked once a
 * session and then drawn as one quad a Tender a frame. The reach is the
 * only part of the key, because every other figure is a compile-time
 * value of `FLEET_AURA`.
 *
 * Both are baked WHITE: the layer tints the quad with the aura's ink, and
 * the alphas the mark's shape needs ride inside the cell. The mesh is the
 * exception. Its whole sheet breathes, so its lines are baked at alpha 1
 * and the quad carries `lineAlpha` and the breath together.
 *
 * A reach that would not fit one cell answers null, and the layer draws
 * the AUTO wave instead (`auraCellFits`).
 */
export function auraSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p[0] !== 'aura' || p.length !== 3) return null;
  const r = Number(p[2]);
  if (!auraCellFits(r)) return null;
  const size = auraCellSize(r);
  const f: AuraFigures = FLEET_AURA;
  switch (p[1]) {
    case 'bubble':
      return {
        key,
        scale: GRADIENT_SCALE,
        box: centredBox(size, size),
        draw(ctx) {
          const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
          g.addColorStop(0, 'rgba(255,255,255,0)'); // mask
          g.addColorStop(f.bubble.midStop, `rgba(255,255,255,${f.bubble.midAlpha})`); // mask
          g.addColorStop(1, `rgba(255,255,255,${f.bubble.rimAlpha})`); // mask
          ctx.beginPath();
          ctx.arc(0, 0, r, 0, TAU);
          ctx.fillStyle = g;
          ctx.fill();
        },
      };
    case 'mesh':
      return {
        key,
        scale: GRADIENT_SCALE,
        box: centredBox(size, size),
        draw(ctx) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(0, 0, r, 0, TAU);
          ctx.clip();
          // The weave is NOT the map's hex grid: it is chain mail, on its
          // own pitch. A pointy-top hex of `cellPx` stands 1.5 cellPx
          // along x and sqrt(3) cellPx along y.
          const cell = f.mesh.cellPx;
          const dx = cell * 1.5, dy = cell * Math.sqrt(3);
          ctx.lineWidth = f.mesh.lineW;
          ctx.strokeStyle = '#ffffff'; // mask
          const cols = Math.ceil(r / dx) + 1, rows = Math.ceil(r / dy) + 1;
          for (let col = -cols; col <= cols; col++) {
            for (let row = -rows; row <= rows; row++) {
              const x = col * dx, y = row * dy + (col % 2 ? dy / 2 : 0);
              if (Math.hypot(x, y) > r + cell) continue;
              ctx.beginPath();
              hexPath(ctx, x, y, cell);
              ctx.stroke();
            }
          }
          ctx.restore();
        },
      };
    default:
      return null;
  }
}

/**
 * One hull master of an alternative ship icon set (R835):
 * `hull:<set>:<cls>:<hollow>`.
 *
 * The AUTO set is baked eagerly, and the eleven others resolve here, so a
 * reader whose Marks theme names one set pays for that set alone. A key that
 * names a set, a class or a hollow flag this build does not carry answers
 * null, the way `badgeSpecFor` and `siteSpecFor` refuse.
 *
 * The DART is not resolved here. It is the same shape in every set, so it is
 * baked one time under the AUTO set and `fleets.ts` asks for it under that
 * set whatever the reader picked.
 */
export function hullSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p[0] !== 'hull') return null;
  if (p.length === 5) return liveryHullSpecFor(p, key);
  if (p.length !== 4) return null;
  const style = oneOf<ShipStyle>(SHIP_STYLES, p[1]) ?? null;
  if (!style) return null;
  const cls = Number(p[2]);
  if (!Number.isInteger(cls) || !HULL_FIELD_OF_CLASS.has(cls)) return null;
  if (p[3] !== '0' && p[3] !== '1') return null;
  return hullSpec(style, cls, hullPathOf(style, cls), p[3] === '1', false);
}

/** The support classes: the one needle of R835 and its extras. */
const SUPPORT_CLASSES = new Set<number>(SUPPORT_ICONS.map(([cls]) => cls));

/**
 * The hull path a liveried cell bakes on: the dart of the AUTO set for a
 * contact whose class the server withheld, else the class of that set. A key
 * that names anything else answers null.
 */
function liveryHullPath(style: ShipStyle, cls: number): HullPath | null {
  if (cls === ShipClass.SHIP_CLASS_UNSPECIFIED) return style === DEFAULT_SHIP_STYLE ? dartPath : null;
  if (!Number.isInteger(cls) || !HULL_FIELD_OF_CLASS.has(cls)) return null;
  return hullPathOf(style, cls);
}

/** One liveried cell, of either key shape. */
function liverySpec(
  key: string, style: ShipStyle, cls: number, code: string, master: LiveryMaster,
): AtlasSpec | null {
  const path = liveryHullPath(style, cls);
  const row = liveryOfCode(code);
  if (!path || !row) return null;
  const hull = { support: SUPPORT_CLASSES.has(cls), span: hullSpan(path) };
  return {
    key,
    scale: HULL_SCALE,
    draw: (ctx) => bakeLivery(ctx, {
      path, hull, row, master,
      cockpitDot: cls === ShipClass.SHIP_CLASS_UNSPECIFIED,
      hollowStrokePx: FLEET_HOLLOW_STROKE_PX,
    }),
  };
}

/** `hull:<set>:<cls>:<hollow>:<livery>` (R1252). */
function liveryHullSpecFor(p: string[], key: string): AtlasSpec | null {
  if (p[3] !== '0' && p[3] !== '1') return null;
  const style = oneOf<ShipStyle>(SHIP_STYLES, p[1]) ?? null;
  return style ? liverySpec(key, style, Number(p[2]), p[4], p[3] === '1' ? 'hollow' : 'filled') : null;
}

/**
 * The mask cell of a livery (R1252): `livery:<set>:<cls>:<livery>`. It holds
 * the option alone, white and clipped to the hull, and the layer draws it
 * over the plain master for the light step and for your own white hull.
 */
export function liveryMaskSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p.length !== 4 || p[0] !== 'livery') return null;
  const style = oneOf<ShipStyle>(SHIP_STYLES, p[1]) ?? null;
  return style ? liverySpec(key, style, Number(p[2]), p[3], 'mask') : null;
}

/**
 * One baked mark of an effect recipe (R910): `effect:<cell>`.
 *
 * There are three, and all three are baked WHITE at one size: the layer
 * tints the quad with the owner's ink and scales it to the radius its
 * catalog row states. A row that changes its period, its radius or its
 * count therefore bakes nothing new.
 *
 * `render/effect-styles.ts` states the key and this answers it, which is the
 * rule `auraCellKey` and `auraSpecFor` follow: one spelling, two readers.
 */
export function effectSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p[0] !== 'effect' || p.length !== 2) return null;
  const size = EFFECT_CELL_PX;
  switch (p[1]) {
    // The soft disc every flare and every trail grain draws from.
    case 'disc':
      return radialSpec(key, size, [
        [0, 'rgba(255,255,255,1)'], // mask
        [0.4, 'rgba(255,255,255,0.5)'], // mask
        [1, 'rgba(255,255,255,0)'], // mask
      ]);
    // A broader, fainter patch, so a fog blob has no edge at all.
    case 'fog':
      return radialSpec(key, size, [
        [0, 'rgba(255,255,255,0.55)'], // mask
        [0.55, 'rgba(255,255,255,0.25)'], // mask
        [1, 'rgba(255,255,255,0)'], // mask
      ]);
    // A four-point star: two crossed tapers over a small core, which is
    // what reads as a twinkle at one or two px on the glass.
    case 'glint':
      return {
        key,
        scale: GRADIENT_SCALE,
        box: centredBox(size, size),
        draw(ctx) {
          const half = size / 2;
          const waist = size * EFFECT_GLINT_WAIST;
          ctx.fillStyle = '#ffffff'; // mask
          for (let arm = 0; arm < 2; arm++) {
            const a = (arm * Math.PI) / 2;
            const cos = Math.cos(a), sin = Math.sin(a);
            ctx.beginPath();
            ctx.moveTo(cos * half, sin * half);
            ctx.lineTo(-sin * waist, cos * waist);
            ctx.lineTo(-cos * half, -sin * half);
            ctx.lineTo(sin * waist, -cos * waist);
            ctx.closePath();
            ctx.fill();
          }
          radial(ctx, size, [
            [0, 'rgba(255,255,255,1)'], // mask
            [EFFECT_GLINT_WAIST, 'rgba(255,255,255,0.6)'], // mask
            [1, 'rgba(255,255,255,0)'], // mask
          ]);
        },
      };
    default:
      return null;
  }
}

/**
 * The whole resolver chain: a site mark first, then a friendly mark, a drawn
 * group digit, a hull of an alternative ship icon set or a liveried hull, the
 * mask of a livery (R1252), a chart glyph, a chart
 * mark, a baked shield aura, a baked effect cell, a baked combat cell, a baked gather cell,
 * a baked anchoring cell, a map marker cell, the dart of a drill spot, a
 * detonation cell (R2079), the commission mark (R2645), a sea mine cell
 * (R2652), a point of interest mark (R2714), a fairway cell and a pirate
 * cell of round 113 (R2720).
 *
 * The engine hands this to the atlas, so no kind is in the eager spec
 * table and a page bakes only what it draws.
 */
export function atlasSpecFor(key: string): AtlasSpec | null {
  return inkedGlyphSpecFor(key) ?? siteSpecFor(key) ?? fleetMarkSpecFor(key) ?? factionPipSpecFor(key) ?? badgeSpecFor(key)
    ?? hullSpecFor(key) ?? liveryMaskSpecFor(key) ?? chartSpecFor(key) ?? chartMarkSpecFor(key) ?? auraSpecFor(key)
    ?? effectSpecFor(key) ?? combatSpecFor(key) ?? gatherSpecFor(key)
    ?? anchorGroundSpecFor(key) ?? mapMarkerSpecFor(key) ?? drillSpecFor(key)
    ?? detonationSpecFor(key) ?? commissionSpecFor(key) ?? seaObjectSpecFor(key)
    ?? poiSpecFor(key) ?? fairwaySpecFor(key) ?? ghostLightSpecFor(key) ?? pirateSpecFor(key);
}

/**
 * One baked mark of a gather recipe (R1120): `gather:<cell>`.
 *
 * There are four, and all four are baked WHITE at their natural size: the
 * recipe tints the quad with the commodity hue, the cargo brown or the
 * faction ink its context carries, and scales it to the figure its row
 * states. A figure change bakes nothing new.
 *
 * `render/gather-recipe-kit.ts` states the key and this answers it, which is
 * the rule `combatCellKey` and `combatSpecFor` follow: one spelling, two
 * readers. `glow` and `halo` are the two `createRadialGradient` calls of
 * mockup round 055, which a batch cannot make. `wedge` and `chip` are the two
 * filled polygons, so a rotated, non-uniformly scaled quad draws them.
 */
export function gatherSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p[0] !== 'gather' || p.length !== 2) return null;
  const size = GATHER_CELL_PX;
  switch (p[1]) {
    // The soft two-stop ramp of the page's `glow`: full at the middle, gone
    // at the rim.
    case 'glow':
      return radialSpec(key, size, [
        [0, 'rgba(255,255,255,1)'], // mask
        [1, 'rgba(255,255,255,0)'], // mask
      ]);
    // The hollow ring of the page's `aRingGlow`, baked at the one ratio the
    // kit fixes: nothing inside `r - w`, full at `r`, gone at `r + w`. The
    // cell's own radius is the OUTER one, and the gradient starts at the
    // INNER one, so the peak stands at the exact middle of the two.
    case 'halo':
      return radialSpec(key, size, [
        [0, 'rgba(255,255,255,0)'], // mask
        [0.5, 'rgba(255,255,255,1)'], // mask
        [1, 'rgba(255,255,255,0)'], // mask
      ], ((size / 2) * (1 - GATHER_HALO_WIDTH)) / GATHER_HALO_OUTER);
    // `wedge`: the apex at x = 0, the base at x = -1, the half-width 0.5. It
    // is a jaw, a saw tooth and a thruster puff.
    case 'wedge':
      return {
        key,
        scale: GATHER_SHAPE_SCALE,
        draw(ctx) {
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(-GATHER_WEDGE_LEN, 0.5);
          ctx.lineTo(-GATHER_WEDGE_LEN, -0.5);
          ctx.closePath();
          ctx.fillStyle = '#ffffff'; // mask
          ctx.fill();
        },
      };
    // `chip-hollow`: the same diamond, stroked rather than filled. The shaft
    // figure of card 09 wears it, because a shaft must read apart from a
    // gather by SHAPE and never by colour alone (R715, R1123).
    case 'chip-hollow':
      return {
        key,
        scale: GATHER_SHAPE_SCALE,
        draw(ctx) {
          chipPath(ctx);
          ctx.lineWidth = GATHER_CHIP_HOLLOW_W;
          ctx.strokeStyle = '#ffffff'; // mask
          ctx.stroke();
        },
      };
    // `chip`: the satellite chip of the deposit mark at k = 1, so a chip in
    // flight has the proportion of the chips on the field it left.
    case 'chip':
      return {
        key,
        scale: GATHER_SHAPE_SCALE,
        draw(ctx) {
          chipPath(ctx);
          ctx.fillStyle = '#ffffff'; // mask
          ctx.fill();
        },
      };
    default:
      return null;
  }
}

/** The diamond both chip cells trace, in the cell's own units. */
function chipPath(ctx: CanvasRenderingContext2D): void {
  ctx.beginPath();
  ctx.moveTo(0, -GATHER_CHIP_HALF_Y);
  ctx.lineTo(GATHER_CHIP_HALF_X, 0);
  ctx.lineTo(0, GATHER_CHIP_HALF_Y);
  ctx.lineTo(-GATHER_CHIP_HALF_X, 0);
  ctx.closePath();
}

/**
 * The supersampling of the two gather SHAPE cells.
 *
 * Both are baked in a box one to four world px wide, so the hull factor is
 * not enough: a recipe scales a jaw to 5 px and a chip to 3, and the cell
 * must still carry an edge at that size. It is the combat shapes' own
 * factor.
 */
const GATHER_SHAPE_SCALE = 64;

/**
 * The stroke of the hollow chip, in the cell's own units (R1123).
 *
 * The chip is 3.2 by 4.2 units, so a stroke of this width leaves an open
 * middle the reader can see at the size a gather figure sets the chip at. It
 * is a PROPORTION of a baked shape and not a figure of the look, so it stands
 * here and not in the TOML, the way `GATHER_HALO_WIDTH` does.
 */
const GATHER_CHIP_HOLLOW_W = 0.5;

/**
 * One baked mark of a combat recipe (R1007): `combat:<cell>`.
 *
 * There are five, and all five are baked WHITE at their natural size: the
 * recipe tints the quad with the ink its look resolved to and scales it to the
 * figure its row states. A figure change bakes nothing new.
 *
 * `render/combat-recipe-kit.ts` states the key and this answers it, which is
 * the rule `effectCellKey` and `effectSpecFor` follow: one spelling, two
 * readers. `flare` and `dome` are the two `createRadialGradient` calls of
 * mockup rounds 048 to 053, which a batch cannot make. `disc` is a hard filled
 * circle, so a non-uniform quad draws the ellipses of the cards. `dart` and
 * `plate` are the two polygon shapes, so a rotated, non-uniformly scaled quad
 * draws them.
 */
export function combatSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p[0] !== 'combat' || p.length !== 2) return null;
  const size = COMBAT_CELL_PX;
  switch (p[1]) {
    // The soft ramp of `K.flare`, stop for stop: 1 at the middle, 0.48 at the
    // half radius, gone at the rim.
    case 'flare':
      return radialSpec(key, size, [
        [0, 'rgba(255,255,255,1)'], // mask
        [0.5, 'rgba(255,255,255,0.48)'], // mask
        [1, 'rgba(255,255,255,0)'], // mask
      ]);
    // The dome of mockup 051/12: clear in the middle, bright on the rim.
    case 'dome':
      return radialSpec(key, size, [
        [0, 'rgba(255,255,255,0)'], // mask
        [0.72, 'rgba(255,255,255,0.28)'], // mask
        [0.96, 'rgba(255,255,255,1)'], // mask
        [1, 'rgba(255,255,255,0)'], // mask
      ]);
    // A hard filled circle, which a non-uniform quad draws as an ellipse.
    case 'disc':
      return {
        key,
        scale: HULL_SCALE,
        box: centredBox(size, size),
        draw(ctx) {
          ctx.beginPath();
          ctx.arc(0, 0, size / 2, 0, TAU);
          ctx.fillStyle = '#ffffff'; // mask
          ctx.fill();
        },
      };
    // `K.dart`: the nose at x = 0, the tail at x = -1, the half-width 0.5.
    case 'dart':
      return {
        key,
        scale: COMBAT_SHAPE_SCALE,
        draw(ctx) {
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(-COMBAT_DART_LEN, 0.5);
          ctx.lineTo(-COMBAT_DART_LEN * 0.7, 0);
          ctx.lineTo(-COMBAT_DART_LEN, -0.5);
          ctx.closePath();
          ctx.fillStyle = '#ffffff'; // mask
          ctx.fill();
        },
      };
    // `HK.poly` of mockup 052/04 at half-length 1: a three-point plate.
    case 'plate':
      return {
        key,
        scale: COMBAT_SHAPE_SCALE,
        draw(ctx) {
          const s = COMBAT_PLATE_HALF;
          ctx.beginPath();
          ctx.moveTo(s, 0);
          ctx.lineTo(-s * 0.6, s * 0.5);
          ctx.lineTo(-s, -s * 0.4);
          ctx.closePath();
          ctx.fillStyle = '#ffffff'; // mask
          ctx.fill();
        },
      };
    default:
      return null;
  }
}

/**
 * The supersampling of the two combat SHAPE cells.
 *
 * Both are baked in a box one or two world px wide, so the hull factor is not
 * enough: a recipe scales the dart to 14 px and the plate to 4, and the cell
 * must still carry an edge at that size.
 */
const COMBAT_SHAPE_SCALE = 64;

// ---------------------------------------------------------------------------
// The atlas
// ---------------------------------------------------------------------------

/** One measured mark: its cropped canvas and its box against the origin. */
interface Measured {
  crop: HTMLCanvasElement;
  cropW: number; cropH: number;
  geo: { w: number; h: number; ox: number; oy: number };
}

interface Cell extends Measured {
  spec: AtlasSpec;
  x: number; y: number;
}

export interface AtlasOpts {
  /** The first sheet size. It doubles once when a cell does not fit. */
  size?: number;
  /** The sheet ceiling. `FALLBACK_MAX_TEXTURE` unless the engine states more. */
  maxSize?: number;
  /** Bakes a key nobody registered, such as a site mark. */
  resolve?: (key: string) => AtlasSpec | null;
}

export class Atlas {
  canvas: HTMLCanvasElement;
  readonly texture: AtlasTexture;
  private ctx: CanvasRenderingContext2D;
  private scratch: HTMLCanvasElement;
  private scratchCtx: CanvasRenderingContext2D;
  private packer: ShelfPacker;
  private entries = new Map<string, AtlasEntry>();
  private cells = new Map<string, Cell>();
  private readonly maxSize: number;
  private readonly resolve: (key: string) => AtlasSpec | null;
  private glyphListeners = new Set<() => void>();
  private repackListeners = new Set<() => void>();
  private facesDone!: () => void;

  /**
   * Resolves when `refreshGlyphCells` has done its work (R548).
   *
   * A cell baked BEFORE the vendored faces land carries the fallback shape
   * and the fallback advance, and the refresh replaces it. A cell baked
   * AFTER this promise resolves measures against the real face at once, so
   * a piece that waits for it never has to lay a run out twice.
   */
  readonly facesReady: Promise<void> = new Promise((done) => { this.facesDone = done; });

  constructor(makeTexture: (source: HTMLCanvasElement) => AtlasTexture, opts: AtlasOpts = {}) {
    const size = opts.size ?? 1024;
    this.maxSize = opts.maxSize ?? FALLBACK_MAX_TEXTURE;
    this.resolve = opts.resolve ?? siteSpecFor;
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvas.height = size;
    this.ctx = ctx2d(this.canvas);
    this.scratch = document.createElement('canvas');
    this.scratch.width = this.scratch.height = SCRATCH_PX;
    this.scratchCtx = ctx2d(this.scratch);
    this.packer = new ShelfPacker(size, size);
    this.texture = makeTexture(this.canvas);
  }

  get size(): number { return this.canvas.width; }
  has(key: string): boolean { return this.entries.has(key); }
  entry(key: string): AtlasEntry | undefined { return this.entries.get(key); }
  bind(unit: number): void { this.texture.bind(unit); }

  /**
   * The advance of one glyph cell, in world px at scale 1 (R548).
   *
   * It bakes the cell when the atlas does not hold it, because the advance
   * of the proportional face is what the bake measured.
   */
  glyphAdvance(key: string): number { return this.get(key).adv ?? 0; }

  /** Where a glyph cell's baseline sits under its top, in world px. */
  glyphBase(key: string): number { return this.get(key).base ?? 0; }

  /**
   * Subscribes to the glyph refresh, and hands back the call that stops it.
   *
   * The chart furniture rebuilds every piece that writes text when this
   * fires, because a run laid out against the fallback advance no longer
   * fits the cells it draws (its `'font'` rebuild reason).
   */
  onGlyphsRefreshed(fn: () => void): () => void {
    this.glyphListeners.add(fn);
    return () => { this.glyphListeners.delete(fn); };
  }

  /**
   * Subscribes to a repack, and hands back the call that stops it.
   *
   * A grow places every cell again, so every UV changes. The atlas updates
   * each `AtlasEntry` in place, so a layer that holds an entry and writes it
   * every frame needs nothing. A layer that wrote UVs into instances ONCE —
   * a static pack — must pack again when this fires.
   */
  onRepacked(fn: () => void): () => void {
    this.repackListeners.add(fn);
    return () => { this.repackListeners.delete(fn); };
  }

  /** Bakes a whole spec table. */
  addAll(specs: readonly AtlasSpec[]): void {
    for (const s of specs) this.add(s);
  }

  /** Bakes one spec, or returns the entry it already has. */
  add(spec: AtlasSpec): AtlasEntry {
    const had = this.entries.get(spec.key);
    if (had) return had;
    return this.bake(spec);
  }

  /**
   * The entry for a key, baking it through the resolver when it is new.
   * Throws for a key nothing can draw, because a silent miss would show as a
   * mark that is quietly absent.
   */
  get(key: string): AtlasEntry {
    const had = this.entries.get(key);
    if (had) return had;
    const spec = this.resolve(key);
    if (!spec) throw new Error(`fast: no atlas spec for ${key}`);
    return this.bake(spec);
  }

  /**
   * Draws a cell again and uploads only that rectangle. The badge digits use
   * it after `document.fonts.load` resolves. A cell whose ink grew is
   * repacked instead, and the old cell is left where it is.
   */
  redraw(key: string): AtlasEntry | undefined {
    const cell = this.cells.get(key);
    if (!cell) return undefined;
    const measured = this.measure(cell.spec);
    if (measured.cropW === cell.cropW && measured.cropH === cell.cropH) {
      cell.crop = measured.crop;
      cell.geo = measured.geo;
      this.ctx.clearRect(cell.x, cell.y, cell.cropW, cell.cropH);
      this.ctx.drawImage(measured.crop, cell.x, cell.y);
      this.texture.patch(measured.crop, cell.x, cell.y);
      const e = this.entries.get(key)!;
      e.w = measured.geo.w; e.h = measured.geo.h;
      e.ox = measured.geo.ox; e.oy = measured.geo.oy;
      // A chart cell re-measured against the vendored face carries a new
      // advance, even where the box rounded to the same texels (R548).
      e.adv = cell.spec.adv; e.base = cell.spec.base;
      return e;
    }
    // The entry stays in the map, so the bake updates the object a layer
    // may hold instead of leaving that layer with the old cell.
    this.cells.delete(key);
    return this.bake(cell.spec);
  }

  /**
   * Waits for the vendored faces and bakes every glyph cell again (R427,
   * R538, R548): the five control-group badges, the damage figure's eleven
   * glyphs at each of its two weights, and every chart glyph the map has
   * asked for so far.
   *
   * A glyph rasterised before the face lands would carry the fallback
   * shape for the whole session, and a face that never lands must not stop
   * the mark appearing at all — so the first bake happens either way and
   * this only replaces it. Each weight is asked for on its own, because
   * the two weights are two files and one may land without the other.
   *
   * It resolves `facesReady` and fires `onGlyphsRefreshed` whatever
   * happened, so a listener never waits on a face that will not come.
   */
  async refreshGlyphCells(): Promise<void> {
    try {
      await this.loadBadgeFaces();
      await this.loadChartFaces();
    } finally {
      this.facesDone();
      for (const fn of [...this.glyphListeners]) fn();
    }
  }

  /** The badge face, at the badge weight and at the two damage weights. */
  private async loadBadgeFaces(): Promise<void> {
    const fonts = documentFonts();
    if (!fonts?.load) return;
    const family = `"${FLEET_GROUP_BADGE_FONT}"`;
    try {
      await fonts.load(`${FLEET_GROUP_BADGE_WEIGHT} ${BADGE_FONT_PX}px ${family}`);
    } catch {
      return; // The fallback stack is already baked.
    }
    // The `mono` cells alone are set in the face. A drawn alphabet of R833
    // owes the font nothing, so the redraw must not touch its cells.
    for (let n = 1; n <= BADGE_DIGITS; n++) this.redraw(badgeKey('mono', n));
    for (const weight of DAMAGE_WEIGHTS) {
      try {
        await fonts.load(`${weight} ${damageFontPx(weight)}px ${family}`);
      } catch {
        continue; // This weight keeps the fallback it was baked with.
      }
      for (const ch of DAMAGE_GLYPHS) this.redraw(damageGlyphKey(weight, ch));
    }
  }

  /**
   * The two chart faces, and every chart cell baked against a fallback.
   *
   * A page that declares neither face — the arcade match, which draws no
   * furniture at all — is skipped without a request, so a match asks the
   * font machinery for nothing.
   *
   * A chart cell is RE-RESOLVED and not only redrawn: the Fell advance is
   * measured at spec time, so the cell needs a fresh spec to carry the
   * advance of the real face.
   */
  private async loadChartFaces(): Promise<void> {
    const fonts = documentFonts();
    if (!fonts?.load || typeof fonts.forEach !== 'function') return;
    let declared = false;
    fonts.forEach((f) => {
      if (f.family === CHART.text.monoFamily || f.family === CHART.text.fellFamily) declared = true;
    });
    if (!declared) return;
    for (const face of CHART_FACES) {
      try {
        await fonts.load(chartFont(face, CHART_PROBE_PX));
      } catch {
        // This face keeps the fallback its cells were baked with.
      }
    }
    for (const key of [...this.cells.keys()]) {
      if (!key.startsWith('chart:')) continue;
      const cell = this.cells.get(key);
      // An inked chart cell of the light identity re-resolves the same way.
      const spec = chartSpecFor(key) ?? inkedGlyphSpecFor(key);
      if (!cell || !spec) continue;
      cell.spec = spec;
      this.redraw(key);
    }
    // The `tag` deposit writes its letter in the same mono face (R832). The
    // cell is only re-drawn and not re-resolved: its box is the tile, which
    // no face changes.
    for (const key of [...this.cells.keys()]) {
      if (key.startsWith(SITE_DEPOSIT_TAG_PREFIX)) this.redraw(key);
    }
  }

  // -- baking --------------------------------------------------------------

  private measure(spec: AtlasSpec): Measured {
    const s = spec.scale;
    const origin = SCRATCH_PX / 2;
    const sc = this.scratchCtx;
    sc.setTransform(1, 0, 0, 1, 0, 0);
    sc.clearRect(0, 0, SCRATCH_PX, SCRATCH_PX);
    sc.setTransform(s, 0, 0, s, origin, origin);
    sc.beginPath();
    spec.draw(sc);
    sc.setTransform(1, 0, 0, 1, 0, 0);

    const bounds = spec.box
      ? {
        minX: Math.round(origin + spec.box.x * s),
        minY: Math.round(origin + spec.box.y * s),
        maxX: Math.round(origin + (spec.box.x + spec.box.w) * s) - 1,
        maxY: Math.round(origin + (spec.box.y + spec.box.h) * s) - 1,
      }
      : scanAlpha(sc, SCRATCH_PX);
    // A measured mark keeps a transparent margin inside its crop; a mark
    // with a fixed box keeps the box (`INK_PAD`).
    const pad = spec.box ? 0 : INK_PAD;
    const geo = entryGeometry(bounds, s, origin, origin, pad);
    const crop = document.createElement('canvas');
    crop.width = geo.cropW;
    crop.height = geo.cropH;
    const inkW = geo.cropW - pad * 2;
    const inkH = geo.cropH - pad * 2;
    // The source rectangle is the INK, so the bake reads no texel outside
    // the scratch sheet and the margin is transparent by construction.
    ctx2d(crop).drawImage(
      this.scratch, bounds.minX, bounds.minY, inkW, inkH,
      pad, pad, inkW, inkH,
    );
    return { crop, cropW: geo.cropW, cropH: geo.cropH, geo };
  }

  private bake(spec: AtlasSpec): AtlasEntry {
    const m = this.measure(spec);
    const cell = this.place(spec, m, this.alloc(m.cropW + CELL_PAD * 2, m.cropH + CELL_PAD * 2));
    this.texture.patch(m.crop, cell.x, cell.y);
    return this.entries.get(spec.key)!;
  }

  /** Draws a measured mark inside its padded slot, and records the cell and the entry. */
  private place(spec: AtlasSpec, m: Measured, at: { x: number; y: number }): Cell {
    const x = at.x + CELL_PAD;
    const y = at.y + CELL_PAD;
    this.ctx.drawImage(m.crop, x, y);
    const cell: Cell = { spec, crop: m.crop, x, y, cropW: m.cropW, cropH: m.cropH, geo: m.geo };
    this.cells.set(spec.key, cell);
    // An entry the atlas already handed out is updated IN PLACE: a layer
    // keeps the object from its constructor, and a new object would leave
    // it drawing the old UVs after a grow or a repacked redraw.
    const fresh = this.makeEntry(cell);
    const had = this.entries.get(spec.key);
    if (had) {
      had.adv = undefined;
      had.base = undefined;
      Object.assign(had, fresh);
    } else this.entries.set(spec.key, fresh);
    return cell;
  }

  private makeEntry({ spec, x, y, cropW, cropH, geo }: Cell): AtlasEntry {
    const size = this.canvas.width;
    if (spec.collapse) {
      const u = (x + cropW / 2) / size;
      const v = (y + cropH / 2) / size;
      return { key: spec.key, u0: u, v0: v, u1: u, v1: v, w: 1, h: 1, ox: 0, oy: 0 };
    }
    const uv = entryUV(x, y, cropW, cropH, size, size);
    return {
      key: spec.key, ...uv, w: geo.w, h: geo.h, ox: geo.ox, oy: geo.oy,
      adv: spec.adv, base: spec.base,
    };
  }

  private alloc(w: number, h: number): { x: number; y: number } {
    const at = this.packer.alloc(w, h);
    if (at) return at;
    if (this.canvas.width >= this.maxSize) {
      throw new Error(`fast: the atlas is full at ${this.canvas.width}px`);
    }
    this.grow();
    const again = this.packer.alloc(w, h);
    if (!again) throw new Error(`fast: the atlas cannot hold a ${w}x${h} cell`);
    return again;
  }

  /** Doubles the sheet and places every cell again from its cropped canvas. */
  private grow(): void {
    const size = Math.min(this.maxSize, this.canvas.width * 2);
    const next = document.createElement('canvas');
    next.width = next.height = size;
    this.canvas = next;
    this.ctx = ctx2d(next);
    this.packer.reset(size, size);
    // The entries stay: `place` rewrites each one in place.
    const cells = [...this.cells.values()];
    this.cells.clear();
    for (const c of cells) {
      const at = this.packer.alloc(c.cropW + CELL_PAD * 2, c.cropH + CELL_PAD * 2);
      if (!at) throw new Error('fast: the atlas lost a cell while growing');
      this.place(c.spec, c, at);
    }
    this.texture.setSource(next);
    for (const fn of [...this.repackListeners]) fn();
  }

  dispose(): void { this.texture.dispose(); }
}

function ctx2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('fast: no 2D canvas context for the atlas');
  return ctx;
}

/** The page's font set, or undefined where there is no document (jsdom). */
function documentFonts(): FontFaceSet | undefined {
  return typeof document === 'undefined' ? undefined : document.fonts;
}

/** The inclusive bounds of every texel whose alpha is not zero. */
function scanAlpha(ctx: CanvasRenderingContext2D, size: number): InkBounds {
  const px = ctx.getImageData(0, 0, size, size).data;
  let minX = size, minY = size, maxX = -1, maxY = -1;
  for (let y = 0; y < size; y++) {
    const row = y * size * 4;
    for (let x = 0; x < size; x++) {
      if (px[row + x * 4 + 3] === 0) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  // An empty mark still needs a cell, or the entry would carry a negative
  // size and the batch would draw a mirrored quad.
  if (maxX < 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}
